import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { GeoBuilder, BOX, CYL, CONE, SPH, ICO, taperBox, chineseRoof, rockGeo, tentGeo } from './geom.js';
import { SITES, FACTION, BRIDGE, RIVER } from './layout.js';
import { ORNAMENT } from '../core/theme.js';
import { tileFbm, tileWorley, colorTexture, normalTexture, canvas, canvasTexture } from './texgen.js';
import { clamp01 } from '../core/mathx.js';
import { surfaceMaterial } from './mat.js';
import { WALLS } from './walls.js';

/**
 * Forts, camps, the river crossing and the hilltop command post.
 *
 * Every location is assembled from primitives (rammed-earth walls with a
 * batter, crenellations, tiled roofs with flying eaves, timber palisades) and
 * baked down to one mesh per material, so a whole fortress is three draw calls.
 * Every location flies a faction banner whose cloth is animated in the vertex
 * shader — that silhouette is what reads at 200 m.
 *
 * Publishes w.bases = { list, nearest(x,z), setTeam(id,team), group }.
 */
export function createBases() {
  const group = new THREE.Group(); group.name = 'bases';
  const list = [];
  const banners = { mesh: null, ranges: new Map(), color: new THREE.Color() };
  let fireMesh = null, fireUni = null, bannerUni = null;
  let wallGroup = null;
  let glowMesh = null;

  /* ============================== textures ============================== */

  function masonryTex() {
    const N = 256;
    const grit = tileFbm(N, 48, 331, 4, 0.55);
    const fine = tileFbm(N, 140, 337, 2, 0.5);
    // Metres-scale weathering, and it is the whole point of this pass.
    // Everything else in this texture varies at one course or finer -- the
    // joints are 2 texels in 32, which lands at about a pixel once a 54 m
    // curtain wall is 25 m from the camera -- so it all mips away and the
    // wall reads as one blank cream field at exactly the range the player
    // sees it from. Patches this coarse are the only variation that survives.
    const weather = tileFbm(N, 5, 911, 4, 0.62);
    // per-column noise, sampled from a single row so it runs as a stripe
    const streak = tileFbm(N, 34, 617, 3, 0.55);
    const d = new Uint8Array(N * N * 4);
    const hgt = new Float32Array(N * N);
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const k = (j * N + i) * 4;
      // horizontal ramming courses every 32 texels + block joints
      const course = Math.abs(((j % 32) - 16)) / 16;
      const band = 0.80 + 0.20 * Math.pow(course, 0.6);
      const joint = (j % 32 < 2) ? 0.62 : 1.0;
      const row = Math.floor(j / 32);
      const off = (row % 2) * 34;
      const vseam = (((i + off) % 68) < 2 && j % 32 > 3) ? 0.70 : 1.0;
      const wv = 0.84 + 0.30 * weather[j * N + i];
      // water staining: runs down from each course lip and fades before the
      // next, which is what puts vertical structure on a horizontal surface
      const below = (j % 32) / 32;
      const fade = (1 - below) * (1 - below);
      const drip = 1 - 0.24 * streak[i] * fade;
      // The 0.72 is not a brightness taste, it is a clipping fix. These terms
      // multiply to about 1.38 at their joint maximum, so a large share of
      // texels used to hit clamp01 and come out at a flat 1.0 -- which is why
      // adding weathering above changed the rendered wall's variation by 0.5
      // of a luminance step. Scaled so the product lands under 1, every one
      // of these layers reaches the screen, and the wall stops being cream.
      let v = clamp01(band * joint * vseam * wv * drip
        * (0.82 + 0.28 * grit[j * N + i]) * (0.90 + 0.20 * fine[j * N + i]) * 0.72);
      d[k] = (238 * v) | 0; d[k + 1] = (232 * v) | 0; d[k + 2] = (222 * v) | 0; d[k + 3] = 255;
      // Relief, built separately from the albedo because they are not the
      // same thing: a mortar joint is a groove, a block face is proud, and
      // water staining is a colour that runs down a surface without cutting
      // into it. Feeding `v` to the Sobel would emboss the drips and flatten
      // the joints, which is a stone wall painted on a stone wall.
      hgt[j * N + i] = clamp01(
        0.62                                        // block face
        + 0.26 * Math.pow(course, 0.7)              // slight crown across the course
        - ((j % 32) < 2 ? 0.52 : 0.0)               // bedding joint, cut deep
        - ((((i + off) % 68) < 2 && j % 32 > 3) ? 0.34 : 0.0)   // vertical seam
        - 0.10 * (1 - weather[j * N + i])           // eroded patches sink
        + 0.09 * (grit[j * N + i] - 0.5)            // stone grain
        + 0.04 * (fine[j * N + i] - 0.5));
    }
    return {
      map: colorTexture(d, N, N, { repeat: true, srgb: true, aniso: 4, mips: true }),
      normal: normalTexture(N, hgt, { bump: 3.4, aniso: 4, mips: true }),
    };
  }

  function woodTex() {
    const N = 256;
    const grain = tileFbm(N, 10, 733, 4, 0.6);
    const fine = tileFbm(N, 90, 739, 2, 0.5);
    const d = new Uint8Array(N * N * 4);
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const k = (j * N + i) * 4;
      const plank = (i % 42 < 2) ? 0.55 : 1.0;        // plank gaps, grain runs vertically
      const streak = 0.72 + 0.30 * Math.abs(Math.sin((i * 0.6 + grain[j * N + i] * 26) * 1.1));
      let v = plank * streak * (0.86 + 0.26 * fine[j * N + i]);
      v = clamp01(v);
      d[k] = (240 * v) | 0; d[k + 1] = (226 * v) | 0; d[k + 2] = (206 * v) | 0; d[k + 3] = 255;
    }
    return colorTexture(d, N, N, { repeat: true, srgb: true, aniso: 4, mips: true });
  }

  function roofTex() {
    const N = 256;
    const grit = tileFbm(N, 64, 911, 3, 0.5);
    const d = new Uint8Array(N * N * 4);
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const k = (j * N + i) * 4;
      // barrel tiles: rounded ribs across x, courses down y
      const rib = Math.sin(((i % 16) / 16) * Math.PI);
      const gap = ((i % 16) < 1) ? 0.42 : 1.0;
      const course = ((j % 22) < 2) ? 0.66 : 1.0;
      let v = (0.46 + 0.72 * Math.pow(rib, 0.55)) * gap * course * (0.86 + 0.28 * grit[j * N + i]);
      v = clamp01(v);
      d[k] = (232 * v) | 0; d[k + 1] = (236 * v) | 0; d[k + 2] = (246 * v) | 0; d[k + 3] = 255;
    }
    return colorTexture(d, N, N, { repeat: true, srgb: true, aniso: 4, mips: true });
  }

  function clothTex() {
    const N = 128;
    const w = tileWorley(N, 18, 1301, 0.8);
    const d = new Uint8Array(N * N * 4);
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const k = (j * N + i) * 4;
      const weave = 0.90 + 0.10 * (Math.sin(i * 0.42) * 0.5 + Math.sin(j * 0.39) * 0.5);
      const v = clamp01(weave * (0.88 + 0.22 * w[j * N + i]));
      d[k] = (245 * v) | 0; d[k + 1] = (242 * v) | 0; d[k + 2] = (234 * v) | 0; d[k + 3] = 255;
    }
    return colorTexture(d, N, N, { repeat: true, srgb: true, aniso: 4, mips: true });
  }

  /** Banner cloth: r = weave shading, g = trim mask, b = glyph mask. */
  function bannerTex() {
    const W = 128, H = 192;
    const { c, g } = canvas(W, H);
    g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);
    // weave shading in red
    const img = g.getImageData(0, 0, W, H);
    const wv = tileFbm(128, 26, 1777, 3, 0.55);
    for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
      const k = (j * W + i) * 4;
      const n = wv[(j % 128) * 128 + i];
      const shade = 0.80 + 0.30 * n + 0.08 * Math.sin(i * 0.5);
      const edge = 1 - 0.35 * Math.pow(Math.max(0, (i - W * 0.55) / (W * 0.45)), 2);
      img.data[k] = Math.min(255, 255 * shade * edge) | 0;
      img.data[k + 1] = 0; img.data[k + 2] = 0; img.data[k + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    // trim (green channel): border bands + tassel row
    g.globalCompositeOperation = 'lighter';
    g.fillStyle = '#00ff00';
    g.fillRect(0, 0, W, 10); g.fillRect(0, H - 10, W, 10);
    g.fillRect(W - 9, 0, 9, H);
    for (let i = 0; i < 9; i++) g.fillRect(W - 22, 6 + i * (H - 12) / 9, 6, 8);
    // glyph (blue channel): stylised military character, drawn as strokes
    g.fillStyle = '#0000ff';
    const cx = W * 0.42, cy = H * 0.5, s = Math.min(W, H) * 0.30;
    const bar = (x, y, w2, h2) => g.fillRect(cx + x * s - w2 * s / 2, cy + y * s - h2 * s / 2, w2 * s, h2 * s);
    bar(0, -0.86, 1.35, 0.20);
    bar(0, -0.44, 0.95, 0.18);
    bar(0, 0.02, 1.50, 0.20);
    bar(0, 0.48, 1.05, 0.18);
    bar(0, 0.88, 1.35, 0.20);
    bar(-0.02, 0, 0.20, 1.90);
    bar(-0.62, -0.62, 0.18, 0.62);
    bar(0.60, -0.62, 0.18, 0.62);
    g.globalCompositeOperation = 'source-over';
    return canvasTexture(c, { repeat: false, srgb: false, aniso: 4 });
  }

  function fireTex() {
    const N = 64;
    const d = new Uint8Array(N * N * 4);
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const k = (j * N + i) * 4;
      const x = (i / N) * 2 - 1, y = j / N;                 // y 0 bottom
      const w = 0.85 * (1 - y) * (0.35 + 0.75 * (1 - y));
      const inside = clamp01(1 - Math.abs(x) / Math.max(0.02, w));
      const v = Math.pow(inside, 1.6) * Math.pow(1 - y, 0.65);
      d[k] = (255 * Math.min(1, v * 1.6)) | 0;
      d[k + 1] = (255 * Math.min(1, v * v * 1.5)) | 0;
      d[k + 2] = (255 * v * v * v * 0.9) | 0;
      d[k + 3] = (255 * v) | 0;
    }
    return colorTexture(d, N, N, { repeat: false, srgb: true, aniso: 1, mips: true });
  }

  /* ============================== materials ============================= */

  // Anisotropy 4, not 1. Every one of these is a DIRECTIONAL tiling set —
  // brick courses, plank gaps, roof ribs, cloth weave — and a fort is almost
  // always seen along a wall or up under an eave, i.e. at a grazing angle.
  // With anisotropy 1 the sampler picks the mip for the LONG axis of the pixel
  // footprint, so the courses and the plank gaps are resolved to their own
  // average from a few metres out and the masonry reads as a flat tan sheet.
  // The detail was authored, generated and uploaded correctly and then thrown
  // away at fetch time. Four small textures, so this costs a fraction of what
  // the same change on the ground would.
  const masonry = masonryTex();
  const texEarth = masonry.map, texEarthN = masonry.normal;
  const texWood = woodTex(), texRoof = roofTex(), texCloth = clothTex();
  const matEarth = surfaceMaterial({ map: texEarth, normalMap: texEarthN, normalScale: [1.15, 1.15],
    wrap: 0.26, hemi: 1.45, spec: [18, 0.05], floor: 0x2b3646 });
  const matWood = surfaceMaterial({ map: texWood, wrap: 0.30, hemi: 1.4, spec: [16, 0.04], floor: 0x27303e });
  // Roofs are two-sided: a chineseRoof panel is a single surface, so under an
  // eave or inside the gatehouse a front-face-only roof shows the sky through
  // itself. The old spec lobe ([44, 0.42]) also laid a blown white sheet
  // across whichever slope faced the sun.
  const matRoof = surfaceMaterial({ map: texRoof, wrap: 0.26, hemi: 1.30, spec: [28, 0.10], floor: 0x2c3746, side: THREE.DoubleSide });
  const matCloth = surfaceMaterial({ map: texCloth, wrap: 0.55, hemi: 1.5, spec: [10, 0.05], floor: 0x333d4c, side: THREE.DoubleSide });

  const PAL = {
    earth: 0xbda071, earthDark: 0x94794f, earthPale: 0xd6bb8c,
    stone: 0x8e8b83, stoneDark: 0x6d6a63, stonePale: 0xa9a69d,
    roof: 0x424b5c, roofDark: 0x2e3543, roofRidge: 0x59637a, roofTerra: 0x7d4436,
    beam: 0x8d3a2c, beamDark: 0x5f271d, wood: 0x6d5535, woodDark: 0x4a3823, woodPale: 0x9a7c4e,
    canvasA: 0xdccca0, canvasB: 0xc4b189, canvasDark: 0x9c8a66,
    gold: 0xd9b25a, iron: 0x4a4e56,
  };
  /** What a `wallRun` is made of. These are the colours every fort wall has
   *  always used; an arena that builds in another material passes its own. */
  const WALL_PAL = {
    footing: PAL.stoneDark, body: PAL.earth, string: PAL.earthDark, cap: PAL.stonePale,
    walk: PAL.stone, kerb: PAL.woodDark, parapet: PAL.earthPale, merlon: PAL.earthPale,
    merlonCap: PAL.stoneDark, slit: 0x2b2419,
  };
  /** Majapahit fired brick — Trowulan's walls, Bajang Ratu, Wringin Lawang.
   *  Multiplied into the same masonry texture as the earth walls, so these are
   *  darker and redder than the brick they stand for. */
  const BRICK = {
    body: 0xb0583c, dark: 0x83402b, deep: 0x5e2c1f, pale: 0xc98262, cap: 0xa39b8a,
  };
  const BRICK_PAL = {
    footing: 0x6b625a, body: BRICK.body, string: BRICK.dark, cap: BRICK.cap,
    walk: 0x8a8378, kerb: PAL.woodDark, parapet: BRICK.pale, merlon: BRICK.body,
    merlonCap: BRICK.dark, slit: 0x2a1812,
  };

  /* ========================= architectural pieces ====================== */

  /**
   * Rammed-earth curtain wall between two points.
   *
   * The run is a *solid body* first — sunk footing, battered core, capping
   * course, rampart walk — and only then a continuous parapet with merlons
   * standing on top of it. The old version emitted its body box with the
   * length along local X while the yaw convention (atan2(dx,dz)) puts the
   * length along local Z, so every wall body was rotated 90 degrees off its
   * own line: the merlons were placed correctly and the wall underneath them
   * was somewhere else entirely. That is why whole runs read as a dotted line
   * of cubes hanging over open ground.
   *
   * Winding matters: the caller must trace the perimeter so that
   * n = (-uz, ux) points *out* of the enclosure, because the parapet and the
   * merlons sit on the outer edge and the walkway on the inner one.
   */
  function wallRun(be, bwd, x0, z0, x1, z1, h, tBase, tTop, opts = {}) {
    const dx = x1 - x0, dz = z1 - z0;
    const L0 = Math.hypot(dx, dz);
    if (L0 < 0.2) return;
    const ux = dx / L0, uz = dz / L0;
    const nx = -uz, nz = ux;                          // outward face normal
    const ang = Math.atan2(dx, dz);                   // local +Z runs along the wall
    const ext = opts.ext ?? (tBase * 0.55);           // overlap so corners never crack
    // Per-end overrides: a run that ends at a gate jamb must stop short of the
    // tower's inner face (a flush end is a coplanar pair that z-fights in the
    // passage), and two pieces of one straight run butt-join rather than
    // overlap (overlapping collinear boxes share side planes and flicker).
    // Omitted, both ends take `ext` and this is the run it always was.
    const e0 = opts.ext0 ?? ext, e1 = opts.ext1 ?? ext;
    const L = L0 + e0 + e1;
    const sh = (e1 - e0) * 0.5;
    const cx = (x0 + x1) / 2 + ux * sh, cz = (z0 + z1) / 2 + uz * sh;
    const Y = opts.y0 ?? 0;                           // world-space runs stand on the ground
    const P = opts.pal ?? WALL_PAL;
    const col = opts.col ?? P.body;
    /** box on the wall line, offset `o` metres along the outward normal */
    const at = (bld, c, o, y, tx, ty, uvs) =>
      bld.put(BOX(), c, cx + nx * o, Y + y, cz + nz * o, 0, ang, 0, tx, ty, L, uvs ?? 1);

    // One texture repeat every ~5.3 m in BOTH axes, which is where the
    // masonry's eight courses land at 0.66 m each — a rammed-earth course.
    // The single scalar these used to pass gave the long face the same repeat
    // count vertically as horizontally, so a 7 m wall wore eighty courses of
    // 8.5 cm and mipped to a cream sheet at any range a player sees it from.
    // The tower next door was already near this density, which is exactly why
    // the tower showed its blocks and the curtain never did.
    const D = 0.19;
    // footing — driven well under the pad so the wall never shows a floating
    // dark edge or an unlit under-face where it meets the ground
    be.put(taperBox(tBase + 1.15, L, tBase + 0.28, L, 3.6), P.footing,
      cx, Y - 2.5, cz, 0, ang, 0, 1, 1, 1, [L * 0.22, 3.6 * 0.22]);
    // battered core: the wall body itself
    be.put(taperBox(tBase, L, tTop, L, h - 1.0), col, cx, Y + 1.0, cz, 0, ang, 0, 1, 1, 1, [L * D, (h - 1.0) * D]);
    // string course, capping, rampart walk
    at(be, P.string, 0, h - 0.62, tTop + 0.36, 0.26, L * 0.30);
    at(be, P.cap, 0, h + 0.17, tTop + 0.30, 0.34, L * 0.30);
    at(be, P.walk, -tTop * 0.15, h + 0.44, tTop * 0.70, 0.18, L * 0.30);
    // inner kerb rail so the walkway has an edge to read against
    bwd.put(BOX(), P.kerb, cx - nx * tTop * 0.48, Y + h + 0.74, cz - nz * tTop * 0.48,
      0, ang, 0, 0.15, 0.46, L, L * 0.4);
    // continuous parapet along the outer edge …
    at(be, P.parapet, tTop * 0.27, h + 0.80, tTop * 0.46, 0.94, L * 0.30);
    if (opts.crenel === false) return;
    // … and merlons standing on it, with embrasures between
    const n = Math.max(2, Math.round(L0 / 2.7));
    const step = L0 / n;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const px = x0 + dx * t + nx * tTop * 0.27, pz = z0 + dz * t + nz * tTop * 0.27;
      be.put(BOX(), P.merlon, px, Y + h + 1.70, pz, 0, ang, 0, tTop * 0.48, 0.86, step * 0.60, 1.4);
      be.put(BOX(), P.merlonCap, px, Y + h + 2.17, pz, 0, ang, 0, tTop * 0.54, 0.16, step * 0.66, 1.4);
      // arrow slit through the merlon face
      be.put(BOX(), P.slit, px + nx * tTop * 0.22, Y + h + 1.72, pz + nz * tTop * 0.22,
        0, ang, 0, 0.10, 0.46, step * 0.16, 1);
    }
  }

  /** Square tapered tower with a tiled hip roof. */
  function tower(b, x, z, w, h, roofRise, rot = 0) {
    b.put(taperBox(w, w, w * 0.86, w * 0.86, h), PAL.earth, x, 0, z, 0, rot, 0, 1, 1, 1, [w * 0.19, h * 0.19]);
    b.put(BOX(), PAL.earthDark, x, h + 0.16, z, 0, rot, 0, w * 0.95, 0.32, w * 0.95, w * 0.2);
    // timber balcony
    const bw = w * 1.16;
    b.put(BOX(), PAL.woodDark, x, h + 0.5, z, 0, rot, 0, bw, 0.18, bw, bw * 0.3);
    for (let i = 0; i < 4; i++) {
      const a = rot + i * Math.PI / 2;
      const rx = x + Math.sin(a) * bw * 0.48, rz = z + Math.cos(a) * bw * 0.48;
      b.put(BOX(), PAL.beam, rx, h + 1.34, rz, 0, a, 0, bw, 0.20, 0.20, bw * 0.3);
      for (let k = -2; k <= 2; k++) {
        b.put(BOX(), PAL.beamDark, rx + Math.cos(a) * k * bw * 0.21, h + 1.0, rz - Math.sin(a) * k * bw * 0.21, 0, a, 0, 0.15, 0.9, 0.15, 1);
      }
    }
    // upper storey + roof
    const uh = h + 1.5;
    b.put(BOX(), PAL.earthPale, x, uh + 1.1, z, 0, rot, 0, w * 0.80, 2.2, w * 0.80, w * 0.2);
    b.put(BOX(), PAL.beam, x, uh + 2.25, z, 0, rot, 0, w * 0.92, 0.26, w * 0.92, w * 0.2);
    for (let i = 0; i < 4; i++) {
      const a = rot + i * Math.PI / 2 + Math.PI / 4;
      b.put(BOX(), PAL.beam, x + Math.sin(a) * w * 0.56, uh + 1.1, z + Math.cos(a) * w * 0.56, 0, a, 0, 0.24, 2.3, 0.24, 1);
    }
    return { roofY: uh + 2.2, size: w };
  }

  /**
   * A tiled hip roof: the curved surface plus the things that give a roof its
   * thickness — a fascia board round the whole eave, four hip ridges running
   * corner-to-ridge, a capped main ridge and eave-corner finials. Without the
   * fascia and the hips the surface reads as a folded sheet of paper.
   */
  function roofOn(rb, x, y, z, w, d, over, rise, rot, col = PAL.roof, flick = 0.6) {
    rb.put(chineseRoof(w, d, over, rise, flick, 5), col, x, y, z, 0, rot, 0, 1, 1, 1, 1);
    const hw = w / 2 + over, hd = d / 2 + over;
    const ridgeHalf = Math.max(0.01, (w - d) / 2);
    const cr = Math.cos(rot), sr = Math.sin(rot);
    /** roof-local (lx,lz) → world */
    const wx = (lx, lz) => x + lx * cr + lz * sr;
    const wz = (lx, lz) => z - lx * sr + lz * cr;

    // eave fascia: the roof edge gets real thickness on all four sides
    for (const sz of [-1, 1])
      rb.put(BOX(), PAL.roofDark, wx(0, sz * hd), y - 0.10, wz(0, sz * hd), 0, rot, 0, hw * 2 + 0.24, 0.34, 0.26, hw * 0.6);
    for (const sx of [-1, 1])
      rb.put(BOX(), PAL.roofDark, wx(sx * hw, 0), y - 0.10, wz(sx * hw, 0), 0, rot, 0, 0.26, 0.34, hd * 2 + 0.24, hd * 0.6);

    // four hip ridges, eave corner → ridge end
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      const ax = sx * hw, az = sz * hd, ay = flick;
      const bx = sx * ridgeHalf, bz = 0, by = rise;
      const ddx = bx - ax, ddy = by - ay, ddz = bz - az;
      const len = Math.hypot(ddx, ddy, ddz);
      if (len < 0.3) continue;
      const hz = Math.hypot(ddx, ddz) || 1e-4;
      const rz2 = Math.asin(Math.max(-1, Math.min(1, ddy / len)));   // pitch
      const ry2 = rot + Math.atan2(-ddz, ddx);                       // heading
      const mx = (ax + bx) / 2, mz = (az + bz) / 2, my = (ay + by) / 2;
      rb.put(BOX(), PAL.roofRidge, wx(mx, mz), y + my + 0.10, wz(mx, mz), 0, ry2, rz2, len * 1.02, 0.20, 0.30, len * 0.5);
      // upturned eave-corner tip
      rb.put(BOX(), PAL.gold, wx(ax * 0.985, az * 0.985), y + flick + 0.16, wz(ax * 0.985, az * 0.985),
        0, rot, 0, 0.34, 0.30, 0.34, 1);
    }

    // main ridge with a capped top course
    const ridgeLen = ridgeHalf * 2 + Math.min(1.4, over * 0.8);
    rb.put(BOX(), PAL.roofRidge, x, y + rise + 0.14, z, 0, rot, 0, ridgeLen, 0.34, 0.42, ridgeLen * 0.5);
    rb.put(BOX(), PAL.roofDark, x, y + rise + 0.36, z, 0, rot, 0, ridgeLen * 0.99, 0.16, 0.24, ridgeLen * 0.5);
    for (const sx of [-1, 1])
      rb.put(BOX(), PAL.gold, wx(sx * ridgeLen * 0.5, 0), y + rise + 0.44, wz(sx * ridgeLen * 0.5, 0), 0, rot, 0, 0.24, 0.52, 0.30, 1);
  }

  /**
   * A timber gate leaf: vertical planks with a visible centre split, iron
   * strap bands across the grain, flush stud heads, a ring handle and pintle
   * hinges. (It used to be the wall's own stone-brick texture darkened, with
   * gold spheres poking out of it like beads.)
   */
  function gateLeaf(bw, sx, cx, y0, hgt, wid, zFace, thick) {
    const planks = 5;
    const pw = wid / planks;
    for (let i = 0; i < planks; i++) {
      const px = cx + sx * (i + 0.5) * pw;
      const tone = i % 2 ? 0x7b6038 : 0x8d7044;
      // planks run vertically; a slim shadow gap is left between them
      bw.put(BOX(), tone, px, y0 + hgt * 0.5, zFace, 0, 0, 0, pw * 0.90, hgt, thick, 1.1);
    }
    // iron straps across the grain + flush stud heads
    for (let r = 0; r < 4; r++) {
      const y = y0 + 0.55 + r * (hgt - 1.1) / 3;
      bw.put(BOX(), PAL.iron, cx + sx * wid * 0.5, y, zFace + sx * 0 + thick * 0.52, 0, 0, 0, wid * 0.99, 0.24, thick * 0.30, 1);
      for (let cc = 0; cc < 4; cc++)
        bw.put(CYL(6, 1), 0x8d8f96, cx + sx * (0.5 + cc) * wid * 0.24, y, zFace + thick * 0.66,
          1.5708, 0, 0, 0.10, 0.10, 0.10, 1);
    }
    // vertical stile at the outer edge + the centre stile that makes the split
    bw.put(BOX(), 0x5e4726, cx + sx * wid * 0.985, y0 + hgt * 0.5, zFace + thick * 0.2, 0, 0, 0, 0.26, hgt, thick * 1.2, 1);
    bw.put(BOX(), 0x54401f, cx + sx * 0.09, y0 + hgt * 0.5, zFace + thick * 0.2, 0, 0, 0, 0.18, hgt, thick * 1.25, 1);
    // pintle hinges on the outer stile
    for (const hy of [y0 + hgt * 0.22, y0 + hgt * 0.78]) {
      bw.put(BOX(), PAL.iron, cx + sx * wid * 0.72, hy, zFace + thick * 0.55, 0, 0, 0, wid * 0.5, 0.30, thick * 0.34, 1);
      bw.put(CYL(6, 1), PAL.iron, cx + sx * wid * 0.99, hy, zFace + thick * 0.55, 0, 0, 0, 0.16, 0.62, 0.16, 1);
    }
    // pushou: a gilt boss plate with a knocker ring hanging from it
    const hx = cx + sx * 1.05, hy = y0 + hgt * 0.48, hz = zFace + thick * 0.6;
    bw.put(CYL(10, 1), PAL.gold, hx, hy, hz, 1.5708, 0, 0, 0.46, 0.09, 0.46, 1);
    bw.put(CYL(10, 1), 0x8a6a24, hx, hy, hz + 0.05, 1.5708, 0, 0, 0.27, 0.06, 0.27, 1);
    for (let i = 0; i < 8; i++) {
      const a = -0.5 + (i / 7) * (Math.PI + 1.0);
      bw.put(BOX(), PAL.gold, hx + Math.cos(a) * 0.30, hy - 0.34 + Math.sin(a) * 0.30, hz + 0.02,
        0, 0, a + 1.5708, 0.09, 0.16, 0.09, 1);
    }
  }

  /* ------------------------------- fort -------------------------------- */
  function buildFort(site, out) {
    const be = out.earth, bw = out.wood, br = out.roof, bc = out.cloth;
    const W = 27, D = 23, H = 7.8, T0 = 3.6, T1 = 2.7;   // half-extents
    const gate = 4.6;                                     // half gate width

    // --- perimeter, traced so every run's outward normal (-uz, ux) points
    //     away from the courtyard. Gate faces +z, toward the road.
    wallRun(be, bw, -W, D, -gate, D, H, T0, T1);
    wallRun(be, bw, gate, D, W, D, H, T0, T1);
    wallRun(be, bw, W, D, W, -D, H, T0, T1);
    wallRun(be, bw, W, -D, -W, -D, H, T0, T1);
    wallRun(be, bw, -W, -D, -W, D, H, T0, T1);

    // --- courtyard floor: rammed earth apron, stone parade square, a drain
    //     channel and the paved road that runs gate → keep
    be.put(BOX(), 0x8a7449, 0, -0.30, 0, 0, 0, 0, W * 2 - T1 * 1.2, 0.70, D * 2 - T1 * 1.2, 10);
    be.put(BOX(), PAL.stone, 0, 0.10, 2.0, 0, 0, 0, 26, 0.14, 22, 9);
    be.put(BOX(), PAL.stonePale, 0, 0.13, D - 9.5, 0, 0, 0, 7.4, 0.12, 27, 7);
    for (let i = -6; i <= 6; i++)
      be.put(BOX(), PAL.stoneDark, i * 2.1, 0.16, 2.0, 0, 0, 0, 0.14, 0.06, 21.6, 1);
    for (let i = -5; i <= 5; i++)
      be.put(BOX(), PAL.stoneDark, 0, 0.16, 2.0 + i * 2.0, 0, 0, 0, 25.6, 0.06, 0.14, 1);

    // --- gate: recess, jambs, plank doors, lintel
    be.put(BOX(), PAL.stoneDark, 0, H * 0.5, D, 0, 0, 0, gate * 2 + 1.4, H, T0 + 0.5, 2.2);
    // gate tunnel: lit stone, not a black slot
    be.put(BOX(), PAL.stoneDark, 0, 2.6, D - 0.4, 0, 0, 0, gate * 2 - 0.4, 5.2, T0 + 1.0, 2.4);
    be.put(BOX(), PAL.stone, 0, 0.14, D, 0, 0, 0, gate * 2 - 0.6, 0.28, T0 + 1.4, 2.0);
    for (const sx of [-1, 1])
      be.put(BOX(), PAL.stone, sx * (gate - 0.35), 2.6, D, 0, 0, 0, 0.3, 5.0, T0 + 1.3, 1.6);
    be.put(BOX(), PAL.stone, 0, 5.05, D, 0, 0, 0, gate * 2 - 0.5, 0.34, T0 + 1.3, 1.6);
    for (const sx of [-1, 1]) {
      be.put(BOX(), PAL.stonePale, sx * (gate + 0.35), 2.8, D + T0 * 0.42, 0, 0, 0, 0.8, 5.9, 1.1, 1.6);
      gateLeaf(bw, sx, 0, 0.10, 5.15, gate - 0.15, D + T0 * 0.55, 0.36);
    }
    be.put(BOX(), PAL.stonePale, 0, 5.42, D + T0 * 0.42, 0, 0, 0, gate * 2 + 1.9, 0.55, 1.15, 2);
    be.put(BOX(), PAL.stoneDark, 0, 5.86, D + T0 * 0.42, 0, 0, 0, gate * 2 + 2.5, 0.34, 1.5, 2);
    // a stone plaque over the arch
    be.put(BOX(), PAL.earthDark, 0, 6.5, D + T0 * 0.50, 0, 0, 0, 3.0, 1.0, 0.30, 1);

    // --- gatehouse on top of the gate
    const gy = H + 0.4;
    be.put(BOX(), PAL.earthPale, 0, gy + 1.5, D, 0, 0, 0, gate * 2 + 5.0, 3.0, T0 + 1.2, 3.2);
    bw.put(BOX(), PAL.beam, 0, gy + 3.2, D, 0, 0, 0, gate * 2 + 5.4, 0.4, T0 + 1.8, 3.2);
    for (let i = -3; i <= 3; i++)
      bw.put(BOX(), PAL.beam, i * 1.7, gy + 2.2, D + T0 * 0.62, 0, 0, 0, 0.22, 2.0, 0.22, 1);
    roofOn(br, 0, gy + 3.4, D, gate * 2 + 6.6, T0 + 3.0, 1.5, 2.4, 0, PAL.roof, 0.8);
    roofOn(br, 0, gy + 6.1, D, gate * 2 + 4.2, T0 + 1.6, 1.1, 1.7, 0, PAL.roof, 0.6);

    // --- corner towers
    for (const [tx, tz] of [[-W, -D], [W, -D], [-W, D], [W, D]]) {
      const t = tower(be, tx, tz, 8.4, H + 3.4, 2.4, 0);
      bw.put(BOX(), PAL.woodDark, tx, t.roofY - 2.3, tz, 0, 0, 0, 8.0, 0.2, 8.0, 2.4);
      roofOn(br, tx, t.roofY, tz, 11.0, 11.0, 1.4, 2.6, 0, PAL.roof, 0.75);
    }

    // --- inner keep
    const kz = -D * 0.42;
    be.put(BOX(), PAL.stone, 0, 0.4, kz, 0, 0, 0, 19, 0.8, 14, 4.2);
    be.put(BOX(), PAL.earthPale, 0, 3.4, kz, 0, 0, 0, 17, 5.2, 12, 3.6);
    for (let i = -3; i <= 3; i++) {
      bw.put(BOX(), PAL.beam, i * 2.6, 3.4, kz + 6.1, 0, 0, 0, 0.34, 5.2, 0.34, 1.2);
      bw.put(BOX(), PAL.beam, i * 2.6, 3.4, kz - 6.1, 0, 0, 0, 0.34, 5.2, 0.34, 1.2);
    }
    bw.put(BOX(), PAL.beam, 0, 6.2, kz, 0, 0, 0, 18.4, 0.55, 13.2, 4);
    roofOn(br, 0, 6.5, kz, 20, 15, 2.0, 4.4, 0, PAL.roof, 1.05);
    bw.put(BOX(), PAL.beamDark, 0, 1.6, kz + 6.3, 0, 0, 0, 3.4, 3.2, 0.3, 1.6);
    // keep steps
    for (let i = 0; i < 3; i++)
      be.put(BOX(), PAL.stonePale, 0, 0.14 + i * 0.20, kz + 7.1 + (2 - i) * 0.55, 0, 0, 0, 6.4, 0.24, 0.6, 1.6);

    // --- barracks ranges down both flanks of the courtyard
    for (const sx of [-1, 1]) {
      const bxc = sx * 16.8, bzc = 2.5;
      be.put(BOX(), PAL.stoneDark, bxc, 0.22, bzc, 0, 0, 0, 8.4, 0.44, 19.5, 4);
      be.put(BOX(), PAL.earthPale, bxc, 1.85, bzc, 0, 0, 0, 7.4, 3.0, 18.0, 3.2);
      for (let i = -4; i <= 4; i++)
        bw.put(BOX(), PAL.beam, bxc - sx * 3.75, 1.85, bzc + i * 2.0, 0, 0, 0, 0.30, 3.0, 0.30, 1);
      // doorways + shuttered windows on the courtyard side
      for (const dz of [-6.0, 0.0, 6.0]) {
        bw.put(BOX(), 0x2a2118, bxc - sx * 3.85, 1.15, bzc + dz, 0, 0, 0, 0.22, 2.3, 1.5, 1);
        bw.put(BOX(), PAL.beam, bxc - sx * 3.85, 2.40, bzc + dz, 0, 0, 0, 0.30, 0.30, 2.0, 1);
      }
      for (const dz of [-3.0, 3.0]) {
        bw.put(BOX(), 0x33291d, bxc - sx * 3.85, 2.05, bzc + dz, 0, 0, 0, 0.20, 1.1, 1.7, 1);
      }
      bw.put(BOX(), PAL.beam, bxc, 3.45, bzc, 0, 0, 0, 8.0, 0.34, 18.6, 3);
      roofOn(br, bxc, 3.60, bzc, 19.6, 9.2, 1.1, 2.1, 1.5708, PAL.roof, 0.55);
    }

    // --- wall stairs up to the rampart, both flanks
    for (const sx of [-1, 1]) {
      const bx = sx * (W - T0 * 0.5 - 2.0);
      for (let i = 0; i < 12; i++) {
        const t = (i + 1) / 12;
        const hgt = 0.5 + t * (H - 0.05);
        be.put(BOX(), i % 2 ? PAL.stone : PAL.stonePale, bx, hgt * 0.5, -D + 6.2 + i * 1.28, 0, 0, 0, 3.6, hgt, 1.32, 1.2);
      }
      be.put(BOX(), PAL.stone, sx * (W - T0 * 0.5 - 1.0), (H + 0.5) * 0.5, -D + 22.0, 0, 0, 0, 2.4, H + 0.5, 2.8, 1.4);
      for (let i = 0; i < 5; i++)
        bw.put(CYL(5, 0.9), PAL.woodDark, bx - sx * 1.9, 0.6 + i * (H - 1.2) / 4 * 0.5 + 0.5, -D + 7.0 + i * 3.1, 0, 0, 0, 0.16, 1.5 + i * 0.5, 0.16, 2);
    }

    // --- garrison dressing: racks, crates, barrels, a well, drill posts
    const rack = (rx, rz, rot) => {
      bw.put(BOX(), PAL.woodDark, rx, 1.05, rz, 0, rot, 0, 3.2, 0.18, 0.42, 1.4);
      bw.put(BOX(), PAL.woodDark, rx, 0.30, rz, 0, rot, 0, 3.2, 0.16, 0.42, 1.4);
      for (const s2 of [-1, 1])
        bw.put(BOX(), PAL.woodDark, rx + Math.sin(rot) * s2 * 1.5, 0.7, rz + Math.cos(rot) * s2 * 1.5, 0, rot, 0, 0.18, 1.4, 0.34, 1);
      for (let k = 0; k < 8; k++) {
        const o = (k - 3.5) * 0.38;
        bw.put(CYL(4, 1), PAL.wood, rx + Math.cos(rot) * o, 1.35, rz - Math.sin(rot) * o, 0.05, rot, 0.04, 0.05, 2.7, 0.05);
        bw.put(CONE(4), PAL.iron, rx + Math.cos(rot) * o, 2.72, rz - Math.sin(rot) * o, 0, rot, 0, 0.09, 0.40, 0.09);
      }
    };
    rack(-11.5, D - 8.5, 0); rack(11.5, D - 8.5, 0);
    rack(-8.0, kz + 9.5, 1.5708);
    for (let i = 0; i < 7; i++)
      bw.put(CYL(8, 0.92), PAL.woodPale, -W + 8.0 + (i % 4) * 1.9, 0.55 + (i > 3 ? 1.1 : 0), D - 4.2 - ((i / 4) | 0) * 1.8, 0, i * 0.4, 0, 0.5, 1.1, 0.5, 1);
    for (let i = 0; i < 6; i++)
      bw.put(BOX(), PAL.wood, W - 9.5 + (i % 3) * 1.75, 0.52 + ((i / 3) | 0) * 1.02, D - 4.6, 0, 0.18 * i, 0, 1.6, 1.0, 1.6, 1);
    for (let i = 0; i < 4; i++)
      bw.put(CYL(9, 0.96), 0xbfa25c, W - 13.5 - (i % 2) * 1.5, 0.62, D - 3.2 - ((i / 2) | 0) * 1.5, 1.5708, 0, 0, 0.62, 1.5, 0.62, 1);
    // well
    be.put(CYL(10, 1), PAL.stone, -6.5, 0.62, D - 12.5, 0, 0, 0, 2.4, 1.2, 2.4, 2);
    be.put(CYL(10, 1), 0x1b2026, -6.5, 1.20, D - 12.5, 0, 0, 0, 1.9, 0.10, 1.9, 1);
    for (const s2 of [-1, 1]) bw.put(BOX(), PAL.woodDark, -6.5 + s2 * 1.0, 2.1, D - 12.5, 0, 0, 0, 0.18, 2.6, 0.18, 1);
    bw.put(BOX(), PAL.beam, -6.5, 3.3, D - 12.5, 0, 0, 0, 2.6, 0.22, 0.22, 1);
    // drill posts / straw dummies on the parade square
    for (let i = 0; i < 4; i++) {
      const dx2 = -9.4 + i * 2.4, dz2 = -1.2;
      bw.put(CYL(6, 0.9), PAL.woodDark, dx2, 0.95, dz2, 0, 0, 0, 0.20, 1.9, 0.20, 2);
      bw.put(CYL(7, 0.95), 0x9c8548, dx2, 1.85, dz2, 0, i * 0.5, 0, 0.46, 1.0, 0.46, 1);
      bw.put(BOX(), 0x8d7440, dx2, 2.05, dz2, 0, i * 0.5, 0, 1.7, 0.20, 0.20, 1);
    }
    // brazier stands at the gate mouth and by the keep steps
    for (const [bx2, bz2] of [[-7.0, D - 6.5], [7.0, D - 6.5], [-9.5, kz + 8.0], [9.5, kz + 8.0]]) {
      for (let i = 0; i < 3; i++) {
        const a = i * 2.094;
        bw.put(CYL(4, 0.8), PAL.iron, bx2 + Math.cos(a) * 0.30, 0.62, bz2 + Math.sin(a) * 0.30,
          Math.sin(a) * 0.34, 0, -Math.cos(a) * 0.34, 0.11, 1.35, 0.11, 1);
      }
      be.put(CYL(9, 1.25), PAL.iron, bx2, 1.42, bz2, 0, 0, 0, 1.05, 0.55, 1.05, 1);
      be.put(CYL(9, 1.0), 0x241a12, bx2, 1.66, bz2, 0, 0, 0, 0.92, 0.10, 0.92, 1);
    }

    out.banners.push(
      // the pair that flanks the gate — the fort's faction read from the road
      { x: -gate - 3.4, z: D + 3.4, h: 12.0, s: 2.9 }, { x: gate + 3.4, z: D + 3.4, h: 12.0, s: 2.9 },
      // standards planted along the rampart walk
      { x: -W + 1.0, z: -10, h: 5.6, s: 1.7, y0: H + 0.55 }, { x: -W + 1.0, z: 8, h: 5.6, s: 1.7, y0: H + 0.55 },
      { x: W - 1.0, z: -10, h: 5.6, s: 1.7, y0: H + 0.55 }, { x: W - 1.0, z: 8, h: 5.6, s: 1.7, y0: H + 0.55 },
      { x: -15, z: -D + 1.0, h: 5.6, s: 1.7, y0: H + 0.55 }, { x: 15, z: -D + 1.0, h: 5.6, s: 1.7, y0: H + 0.55 },
      // gatehouse pair
      { x: -gate - 4.8, z: D, h: 5.6, s: 1.7, y0: H + 0.55 }, { x: gate + 4.8, z: D, h: 5.6, s: 1.7, y0: H + 0.55 },
      // the capture standard on the parade square, and the keep's own
      { x: 0, z: 12.5, h: 13.0, s: 3.2 },
      { x: 0, z: kz - 9.0, h: 10.5, s: 2.6 },
    );
    out.fires.push(
      { x: -7.0, z: D - 6.5, s: 0.62, y: 1.55, noRing: true }, { x: 7.0, z: D - 6.5, s: 0.62, y: 1.55, noRing: true },
      { x: -9.5, z: kz + 8.0, s: 0.62, y: 1.55, noRing: true }, { x: 9.5, z: kz + 8.0, s: 0.62, y: 1.55, noRing: true },
      { x: -19, z: -18, s: 1.0 }, { x: 19, z: -18, s: 1.0 },
    );
    out.gates.push({ x: 0, z: D + 1.0, y: 2.6, r: gate * 2 + 2.0 });
    out.spawns.push({ x: 0, z: D - 7 }, { x: -10, z: 4 }, { x: 10, z: 4 }, { x: -14, z: -8 }, { x: 14, z: -8 }, { x: 0, z: 10 });
    out.radius = 36;
  }

  /* ------------------------------- camp -------------------------------- */
  const _tentCache = new Map();
  function tent(b, bc, x, z, rot, w, d, h, col) {
    const key = w + '_' + d + '_' + h + '_' + col;
    let g = _tentCache.get(key);
    if (!g) { g = tentGeo(w, d, h, col, col === PAL.canvasA ? PAL.canvasB : PAL.canvasDark, PAL.woodDark); _tentCache.set(key, g); }
    bc.put(g, 0xffffff, x, 0, z, 0, rot, 0, 1, 1, 1, 1);
  }

  function palisade(b, cx, cz, r, n, gapA, gapW, col = PAL.wood) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      let da = Math.abs(((a - gapA + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
      if (da < gapW) continue;
      const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
      const h = 3.0 + ((i * 37) % 7) * 0.11;
      b.put(CYL(6, 0.88), i % 3 ? col : PAL.woodPale, x, h * 0.5, z, 0, a, 0, 0.34, h, 0.34, 1.4);
      b.put(CONE(6), i % 2 ? col : PAL.woodPale, x, h + 0.28, z, 0, a, 0, 0.34, 0.62, 0.34);
    }
    // horizontal rail
    for (let i = 0; i < n; i++) {
      const a0 = (i / n) * Math.PI * 2, a1 = ((i + 1) / n) * Math.PI * 2;
      let da = Math.abs(((a0 - gapA + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
      if (da < gapW + 0.06) continue;
      const x0 = cx + Math.cos(a0) * r, z0 = cz + Math.sin(a0) * r;
      const x1 = cx + Math.cos(a1) * r, z1 = cz + Math.sin(a1) * r;
      const len = Math.hypot(x1 - x0, z1 - z0);
      // yaw convention is atan2(dx,dz), so the rail's length must run along
      // local Z — along local X it was rotated 90 degrees off the palisade
      const ra = Math.atan2(x1 - x0, z1 - z0);
      b.put(BOX(), PAL.woodDark, (x0 + x1) / 2, 2.15, (z0 + z1) / 2, 0, ra, 0, 0.16, 0.20, len * 1.15, 1);
      b.put(BOX(), PAL.woodDark, (x0 + x1) / 2, 1.05, (z0 + z1) / 2, 0, ra, 0, 0.14, 0.17, len * 1.15, 1);
    }
  }

  function buildCamp(site, out) {
    const be = out.earth, bw = out.wood, br = out.roof, bc = out.cloth;
    const R = 22;
    palisade(bw, 0, 0, R, 78, Math.PI * 0.5, 0.20);
    // gate posts
    for (const s of [-1, 1]) {
      bw.put(CYL(6, 0.9), PAL.woodDark, s * 3.4, 2.1, R, 0, 0, 0, 0.30, 4.2, 0.30);
    }
    bw.put(BOX(), PAL.beam, 0, 4.3, R, 0, 0, 0, 8.4, 0.45, 0.45, 3);

    // command tent
    bc.put(BOX(), PAL.canvasDark, 0, 0.05, -6, 0, 0, 0, 12, 0.1, 9, 4);
    tent(bw, bc, 0, -6, 0, 9.5, 7.0, 4.4, PAL.canvasA);
    bw.put(BOX(), PAL.beam, 0, 4.6, -6, 0, 0, 0, 10.5, 0.22, 0.22, 3);
    // awning in front of it
    bc.put(BOX(), PAL.canvasB, 0, 3.0, -1.2, -0.26, 0, 0, 8.0, 0.08, 5.0, 2.4);
    for (const s of [-1, 1]) bw.put(CYL(5, 1), PAL.woodDark, s * 3.6, 1.5, 1.1, 0, 0, 0, 0.10, 3.0, 0.10);

    // ring of soldier tents
    for (let i = 0; i < 7; i++) {
      const a = -0.9 + i * 0.62;
      const r = 13.5;
      tent(bw, bc, Math.cos(a) * r, Math.sin(a) * r + 2, a + Math.PI / 2, 4.4, 3.4, 2.5, i % 2 ? PAL.canvasA : PAL.canvasB);
    }
    // supplies
    for (let i = 0; i < 9; i++) {
      const a = 2.1 + i * 0.24, r = 15 + (i % 3) * 1.4;
      bw.put(CYL(8, 0.92), PAL.woodPale, Math.cos(a) * r, 0.55, Math.sin(a) * r, 0, a, 0, 0.5, 1.1, 0.5, 1);
    }
    for (let i = 0; i < 6; i++) {
      bw.put(BOX(), PAL.wood, -14 + (i % 3) * 1.7, 0.5 + Math.floor(i / 3) * 1.0, -14, 0, 0.3 * i, 0, 1.5, 1.0, 1.5, 1);
    }
    for (let i = 0; i < 4; i++) {
      bw.put(CYL(9, 0.96), 0xbfa25c, 11 + (i % 2) * 2.6, 0.62, -13 - Math.floor(i / 2) * 2.6, 1.5708, 0, 0, 0.62, 1.5, 0.62, 1);
    }
    // watch platform
    for (const [px, pz] of [[-R * 0.72, -R * 0.72]]) {
      for (const [ox, oz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]])
        bw.put(BOX(), PAL.woodDark, px + ox * 1.2, 2.6, pz + oz * 1.2, 0, 0, 0, 0.28, 5.2, 0.28, 2);
      bw.put(BOX(), PAL.wood, px, 5.3, pz, 0, 0, 0, 3.4, 0.24, 3.4, 1.6);
      for (let i = 0; i < 4; i++) {
        const a = i * Math.PI / 2;
        bw.put(BOX(), PAL.wood, px + Math.sin(a) * 1.6, 5.9, pz + Math.cos(a) * 1.6, 0, a, 0, 3.4, 0.5, 0.09, 1.6);
      }
      roofOn(br, px, 6.3, pz, 4.6, 4.6, 0.8, 1.3, 0.4, PAL.roof, 0.4);
    }

    out.banners.push(
      { x: -3.4, z: R + 0.2, h: 8.0, s: 2.2 }, { x: 3.4, z: R + 0.2, h: 8.0, s: 2.2 },
      { x: 0, z: -11.5, h: 12.0, s: 3.0 },
      { x: -13, z: -2, h: 6.5, s: 1.8 }, { x: 13, z: -2, h: 6.5, s: 1.8 },
      { x: -R * 0.72, z: -R * 0.72, h: 5.0, s: 1.5, y0: 5.45 },
    );
    out.fires.push({ x: -6, z: 6, s: 1.2 }, { x: 7, z: 5, s: 1.0 });
    out.spawns.push({ x: 0, z: 12 }, { x: -8, z: 6 }, { x: 8, z: 6 }, { x: 0, z: -1 }, { x: -12, z: -6 }, { x: 12, z: -6 });
    out.radius = 26;
  }

  /* ------------------------------ bridge ------------------------------- */
  function buildBridge(site, out, w) {
    const be = out.earth, bw = out.wood, br = out.roof;
    // local +x runs across the river (road B is roughly east-west here)
    const span = 34, halfW = 5.0;
    const deckY = 0.0;
    // piers + arches
    for (const px of [-11, 11]) {
      be.put(BOX(), PAL.stoneDark, px, -3.4, 0, 0, 0, 0, 4.6, 7.0, halfW * 2 + 1.2, 2.6);
    }
    for (const cx of [-17, 0, 17]) {
      const rr = cx === 0 ? 6.2 : 4.6;
      const seg = 9;
      for (let i = 0; i < seg; i++) {
        const a0 = Math.PI * (i / seg), a1 = Math.PI * ((i + 1) / seg);
        const am = (a0 + a1) / 2;
        const x = cx - Math.cos(am) * rr, y = -0.9 - (rr - Math.sin(am) * rr) * 0.0;
        be.put(BOX(), PAL.stone, cx - Math.cos(am) * rr, -0.9 - rr + Math.sin(am) * rr, 0,
          0, 0, -am + Math.PI / 2, (Math.PI * rr / seg) * 1.25, 1.0, halfW * 2 + 0.6, 1.6);
      }
    }
    // deck
    be.put(BOX(), PAL.stone, 0, deckY - 0.35, 0, 0, 0, 0, span + 8, 0.7, halfW * 2, 6);
    be.put(BOX(), PAL.stonePale, 0, deckY + 0.03, 0, 0, 0, 0, span + 8, 0.10, halfW * 2 - 0.9, 6);
    // parapets with balusters
    for (const sz of [-1, 1]) {
      be.put(BOX(), PAL.stone, 0, deckY + 0.16, sz * (halfW - 0.35), 0, 0, 0, span + 8, 0.34, 0.60, 6);
      be.put(BOX(), PAL.stonePale, 0, deckY + 1.05, sz * (halfW - 0.35), 0, 0, 0, span + 8, 0.20, 0.76, 6);
      const n = 22;
      for (let i = 0; i < n; i++) {
        const x = -(span + 6) / 2 + (i + 0.5) * (span + 6) / n;
        be.put(CYL(6, 0.62), PAL.stonePale, x, deckY + 0.65, sz * (halfW - 0.35), 0, 0, 0, 0.20, 0.72, 0.20);
      }
      // corner posts with lanterns
      for (const sx of [-1, 1]) {
        be.put(BOX(), PAL.stoneDark, sx * (span + 6) / 2, deckY + 0.9, sz * (halfW - 0.35), 0, 0, 0, 0.8, 2.0, 0.8, 1.4);
        be.put(SPH(7, 5), PAL.gold, sx * (span + 6) / 2, deckY + 2.1, sz * (halfW - 0.35), 0, 0, 0, 0.55, 0.55, 0.55);
      }
    }
    out.banners.push(
      { x: -(span + 6) / 2, z: -halfW - 1.2, h: 7.5, s: 2.0 }, { x: (span + 6) / 2, z: -halfW - 1.2, h: 7.5, s: 2.0 },
      { x: -(span + 6) / 2, z: halfW + 1.2, h: 7.5, s: 2.0 }, { x: (span + 6) / 2, z: halfW + 1.2, h: 7.5, s: 2.0 },
    );
    out.spawns.push({ x: -20, z: 0 }, { x: 20, z: 0 }, { x: 0, z: 0 }, { x: -14, z: 3 }, { x: 14, z: -3 });
    out.radius = 20;
    out.noPad = true;
  }

  /* --------------------------- hilltop post ---------------------------- */
  function buildPost(site, out) {
    const be = out.earth, bw = out.wood, br = out.roof, bc = out.cloth;
    // stone dais
    be.put(CYL(12, 1), PAL.stone, 0, 0.35, 0, 0, 0, 0, 9.0, 0.7, 9.0, 6);
    be.put(CYL(12, 1), PAL.stonePale, 0, 0.75, 0, 0, 0, 0, 8.2, 0.2, 8.2, 6);
    // drum tower on four columns
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2 + Math.PI / 4;
      bw.put(CYL(7, 0.9), PAL.beam, Math.cos(a) * 3.2, 3.0, Math.sin(a) * 3.2, 0, 0, 0, 0.30, 5.0, 0.30, 2);
    }
    bw.put(BOX(), PAL.beamDark, 0, 5.6, 0, 0, 0, 0, 8.4, 0.4, 8.4, 3);
    bw.put(BOX(), PAL.wood, 0, 5.9, 0, 0, 0, 0, 7.6, 0.2, 7.6, 3);
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2;
      bw.put(BOX(), PAL.beam, Math.sin(a) * 3.7, 6.5, Math.cos(a) * 3.7, 0, a, 0, 7.6, 0.5, 0.10, 3);
    }
    roofOn(br, 0, 6.2, 0, 10.5, 10.5, 1.6, 2.4, 0, PAL.roofTerra, 0.85);
    // war drum
    bw.put(CYL(12, 1), PAL.beamDark, 4.5, 1.9, 3.2, 0, 0, 1.5708, 1.1, 1.5, 1.1, 2);
    bw.put(CYL(12, 1), PAL.canvasA, 4.5, 1.9, 3.2, 0, 0, 1.5708, 1.12, 1.45, 1.12, 2);
    for (const s of [-1, 1]) bw.put(BOX(), PAL.wood, 4.5, 0.9, 3.2 + s * 1.1, 0.4 * s, 0, 0, 0.14, 2.2, 0.14, 1);
    // sandbag/earthwork ring + stakes
    for (let i = 0; i < 26; i++) {
      const a = (i / 26) * Math.PI * 2;
      if (Math.abs(((a - Math.PI * 1.5 + Math.PI * 3) % (Math.PI * 2)) - Math.PI) < 0.3) continue;
      const x = Math.cos(a) * 13, z = Math.sin(a) * 13;
      be.put(BOX(), PAL.earthDark, x, 0.45, z, 0, -a - 1.5708, 0, 3.4, 0.9, 1.5, 1.4);
      bw.put(CYL(4, 0.8), PAL.woodDark, x + Math.cos(a) * 1.3, 0.9, z + Math.sin(a) * 1.3, Math.sin(a) * 0.5, 0, -Math.cos(a) * 0.5, 0.13, 2.4, 0.13);
    }
    // map table under an awning
    bc.put(BOX(), PAL.canvasA, -5.5, 3.2, -3.0, -0.2, 0.3, 0, 5.0, 0.08, 4.0, 2);
    for (const [ox, oz] of [[-2.2, -1.7], [2.2, -1.7], [-2.2, 1.7], [2.2, 1.7]])
      bw.put(CYL(5, 1), PAL.woodDark, -5.5 + ox, 1.6, -3.0 + oz, 0, 0, 0, 0.09, 3.2, 0.09);
    bw.put(BOX(), PAL.wood, -5.5, 1.05, -3.0, 0, 0.3, 0, 2.4, 0.12, 1.5, 1);

    out.banners.push(
      { x: -4.2, z: 8.2, h: 11.0, s: 2.6 },
      { x: -8, z: 6, h: 8.5, s: 2.2 }, { x: 8, z: 6, h: 8.5, s: 2.2 },
      { x: -8, z: -6, h: 8.5, s: 2.2 }, { x: 8, z: -6, h: 8.5, s: 2.2 },
    );
    out.fires.push({ x: -9, z: -9, s: 0.9 }, { x: 9, z: -9, s: 0.9 });
    out.spawns.push({ x: 0, z: 11 }, { x: -9, z: 4 }, { x: 9, z: 4 }, { x: 0, z: -11 }, { x: -6, z: -8 });
    out.radius = 24;
  }

  /* ------------------------------ outpost ------------------------------ */
  function buildOutpost(site, out) {
    const be = out.earth, bw = out.wood, br = out.roof, bc = out.cloth;
    const R = 15;
    palisade(bw, 0, 0, R, 58, Math.PI * 0.5, 0.26);
    for (const s of [-1, 1]) bw.put(CYL(6, 0.9), PAL.woodDark, s * 3.0, 2.1, R, 0, 0, 0, 0.28, 4.2, 0.28);
    bw.put(BOX(), PAL.beam, 0, 4.3, R, 0, 0, 0, 7.6, 0.42, 0.42, 3);
    // tall watchtower
    const TH = 12.5;
    for (const [ox, oz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      bw.put(CYL(5, 0.72), PAL.woodDark, ox * 2.4, TH * 0.5, oz * 2.4, ox * 0.055, 0, -oz * 0.055, 0.26, TH, 0.26, 4);
    }
    for (let lvl = 1; lvl <= 3; lvl++) {
      const y = lvl * TH / 4;
      for (let i = 0; i < 4; i++) {
        const a = i * Math.PI / 2;
        bw.put(BOX(), PAL.wood, Math.sin(a) * 2.3, y, Math.cos(a) * 2.3, 0, a, 0, 5.0, 0.16, 0.16, 2);
      }
      bw.put(BOX(), PAL.woodDark, 0, y, 0, 0, 0.78, 0, 6.6, 0.12, 0.12, 2);
    }
    bw.put(BOX(), PAL.wood, 0, TH + 0.2, 0, 0, 0, 0, 6.4, 0.3, 6.4, 3);
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2;
      bw.put(BOX(), PAL.wood, Math.sin(a) * 3.0, TH + 0.9, Math.cos(a) * 3.0, 0, a, 0, 6.4, 0.7, 0.12, 3);
    }
    roofOn(br, 0, TH + 1.4, 0, 7.4, 7.4, 1.1, 1.7, 0, PAL.roofTerra, 0.6);
    tent(bw, bc, -8, -6, 0.6, 4.4, 3.4, 2.5, PAL.canvasB);
    tent(bw, bc, 8, -6, -0.6, 4.4, 3.4, 2.5, PAL.canvasA);
    for (let i = 0; i < 4; i++) bw.put(CYL(8, 0.92), PAL.woodPale, -9 + i * 1.6, 0.55, 5, 0, 0, 0, 0.5, 1.1, 0.5, 1);

    out.banners.push(
      { x: 5.2, z: 4.6, h: 10.5, s: 2.2 },
      { x: -3.0, z: R + 0.2, h: 6.5, s: 1.8 }, { x: 3.0, z: R + 0.2, h: 6.5, s: 1.8 },
    );
    out.fires.push({ x: 0, z: -8, s: 1.0 });
    out.spawns.push({ x: 0, z: 9 }, { x: -7, z: 2 }, { x: 7, z: 2 }, { x: 0, z: -4 });
    out.radius = 20;
  }

  /* ========================= the arena's own walls ===================== */

  /**
   * The walls an arena declares (`walls.js` derives them from its record):
   * brick curtain and court walls, and a gate structure in every gap. Built
   * in WORLD space — one group, not a site — because a 416 m curtain belongs
   * to no holding.
   *
   * A run is cut into pieces of <= 30 m and each piece stands on the lowest
   * ground under its footprint, so the footing never floats; pieces of one
   * straight run butt-join (ext 0) instead of overlapping. The collider for
   * every piece of masonry here is the box `walls.js` already holds, from the
   * same numbers, so what a body bumps into is what the camera sees.
   */
  const PIECE = 30;
  /** Lowest ground under an oriented footprint (centre, axes, half extents). */
  function groundUnder(T, cx, cz, ux, uz, hl, hw) {
    let y = Infinity;
    const nl = Math.max(1, Math.ceil(hl / 3)), nw = 1;
    for (let i = -nl; i <= nl; i++) for (let j = -nw; j <= nw; j++) {
      const a = (i / nl) * hl, b = (j / nw) * hw;
      y = Math.min(y, T.heightAt(cx + ux * a - uz * b, cz + uz * a + ux * b));
    }
    return y;
  }

  function buildArenaWalls(T, out) {
    const be = out.earth, bw = out.wood;
    for (let ri = 0; ri < WALLS.runs.length; ri++) {
      const r = WALLS.runs[ri];
      if (r.mat === 'timber') {
        // logs stand on their own ground, so a stockade needs no pieces
        palisadeRun(T, bw, r.x0, r.z0, r.x1, r.z1, r.h, 1000 + ri * 97, out.fires);
        continue;
      }
      const n = Math.max(1, Math.ceil(r.len / PIECE));
      for (let k = 0; k < n; k++) {
        const a = (r.len * k) / n, b = (r.len * (k + 1)) / n;
        const x0 = r.x0 + r.ux * a, z0 = r.z0 + r.uz * a, x1 = r.x0 + r.ux * b, z1 = r.z0 + r.uz * b;
        const y0 = groundUnder(T, (x0 + x1) / 2, (z0 + z1) / 2, r.ux, r.uz, (b - a) / 2, r.t0 / 2 + 0.6);
        // ends: a jamb stops 0.4 m inside its tower, an internal joint butts,
        // a free end or a corner keeps wallRun's overlap
        const e0 = k > 0 ? 0 : (r.jam0 ? -0.4 : undefined);
        const e1 = k < n - 1 ? 0 : (r.jam1 ? -0.4 : undefined);
        wallRun(be, bw, x0, z0, x1, z1, r.h, r.t0, r.t1, { y0, pal: BRICK_PAL, ext0: e0, ext1: e1 });
      }
    }
    for (const g of WALLS.gates) {
      if (g.kind === 'regol') buildRegol(T, g, out);
      else buildGate(T, g, out);
    }
  }

  /**
   * One tier of a gate tower: a box whose inner face (toward the passage) is
   * pinned at `in0` along the wall and which grows AWAY from the passage by
   * `len`, `dep` deep across the wall, from y to y + h. `s` is the side (+1 /
   * -1 along u). Pinning the inner face is what makes a candi bentar read as
   * one temple split clean down the middle.
   */
  function gateTier(be, g, s, in0, len, dep, y, h, col) {
    const o = s * (in0 + len * 0.5);
    be.put(BOX(), col, g.x + g.ux * o, y + h * 0.5, g.z + g.uz * o, 0, Math.atan2(g.ux, g.uz), 0,
      dep, h, len, [Math.max(1, len * 0.3), Math.max(1, h * 0.3)]);
  }

  /** A tier centred on the passage, spanning both towers (paduraksa crown). */
  function gateSpan(be, g, len, dep, y, h, col) {
    be.put(BOX(), col, g.x, y + h * 0.5, g.z, 0, Math.atan2(g.ux, g.uz), 0,
      dep, h, len, [Math.max(1, len * 0.3), Math.max(1, h * 0.3)]);
  }

  function buildGate(T, g, out) {
    const be = out.earth;
    const G = g.tower;
    const half = g.w * 0.5, tw = G.tw, D = g.t0 + G.proj * 2;
    const Ht = g.h * G.rise;
    // the whole structure stands on the lowest ground under both towers
    const y = groundUnder(T, g.x, g.z, g.ux, g.uz, half + tw, D * 0.5 + 0.8);
    const ang = Math.atan2(g.ux, g.uz);
    // passage floor: a stone threshold the road runs over
    be.put(BOX(), 0x8d877c, g.x, y + 0.02, g.z, 0, ang, 0, D + 3.0, 0.3, g.w + 0.2, [2, 2]);

    if (g.kind === 'bentar') {
      for (const s of [-1, 1]) {
        // kaki: plinth and its moulding
        gateTier(be, g, s, half, tw + 0.5, D + 0.6, y - 1.0, 1.0 + Ht * 0.08, BRICK.dark);
        gateTier(be, g, s, half, tw + 0.3, D + 0.4, y + Ht * 0.08, Ht * 0.025, BRICK.pale);
        // badan: the body, with a recessed panel band and a cornice
        gateTier(be, g, s, half, tw, D, y + Ht * 0.105, Ht * 0.39, BRICK.body);
        gateTier(be, g, s, half, tw + 0.12, D + 0.12, y + Ht * 0.30, Ht * 0.035, BRICK.dark);
        gateTier(be, g, s, half, tw + 0.35, D + 0.35, y + Ht * 0.495, Ht * 0.03, BRICK.pale);
        // atap: five diminishing roof tiers, each with a lip
        let yy = y + Ht * 0.525;
        for (let k = 0; k < 5; k++) {
          const f = 0.9 - k * 0.15;
          const th = Ht * 0.072;
          gateTier(be, g, s, half, tw * f, D * f, yy, th, k % 2 ? BRICK.body : BRICK.dark);
          gateTier(be, g, s, half, tw * f + 0.22, D * f + 0.22, yy + th, Ht * 0.012, BRICK.pale);
          yy += th + Ht * 0.012;
        }
        // mastaka: the crown block and its finial
        gateTier(be, g, s, half, tw * 0.22, D * 0.22, yy, Ht * 0.05, BRICK.dark);
        const o = s * (half + tw * 0.11);
        be.put(CONE(6), BRICK.deep, g.x + g.ux * o, yy + Ht * 0.05 + Ht * 0.035, g.z + g.uz * o,
          0, ang, 0, tw * 0.10, Ht * 0.07, tw * 0.10);
      }
    } else {
      // paduraksa: two piers, a lintel spanning the passage, a stepped crown
      const Wf = g.w + tw * 2;                 // full width over both piers
      const hp = Ht * G.pass;                  // the passage's clear height (walls.js)
      for (const s of [-1, 1]) {
        gateTier(be, g, s, half, tw + 0.5, D + 0.6, y - 1.0, 1.0 + Ht * 0.06, BRICK.dark);
        gateTier(be, g, s, half, tw, D, y + Ht * 0.06, hp - Ht * 0.06, BRICK.body);
        // a slim pilaster at the jamb, proud of the face on both sides
        gateTier(be, g, s, half, 0.45, D + 0.24, y + Ht * 0.06, hp - Ht * 0.06, BRICK.pale);
      }
      // lintel and the body over the passage
      gateSpan(be, g, Wf, D, y + hp, Ht * 0.04, BRICK.pale);
      gateSpan(be, g, Wf, D - 0.2, y + hp + Ht * 0.04, Ht * 0.18, BRICK.body);
      // kala: the guardian mask over the passage, on both faces
      for (const s of [-1, 1]) {
        be.put(BOX(), BRICK.deep, g.x + g.nx * s * (D * 0.5 - 0.02), y + hp + Ht * 0.12, g.z + g.nz * s * (D * 0.5 - 0.02),
          0, ang, 0, 0.36, Ht * 0.10, g.w * 0.34, 1);
      }
      gateSpan(be, g, Wf + 0.5, D + 0.5, y + hp + Ht * 0.22, Ht * 0.03, BRICK.pale);
      // the crown: seven diminishing tiers to a finial
      let yy = y + hp + Ht * 0.25;
      for (let k = 0; k < 7; k++) {
        const f = 0.92 - k * 0.115;
        const th = Ht * 0.047;
        gateSpan(be, g, Wf * f, D * (0.96 - k * 0.07), yy, th, k % 2 ? BRICK.body : BRICK.dark);
        gateSpan(be, g, Wf * f + 0.3, D * (0.96 - k * 0.07) + 0.3, yy + th, Ht * 0.01, BRICK.pale);
        yy += th + Ht * 0.01;
      }
      gateSpan(be, g, Wf * 0.12, D * 0.3, yy, Ht * 0.05, BRICK.dark);
      be.put(CONE(8), BRICK.deep, g.x, yy + Ht * 0.05 + Ht * 0.04, g.z, 0, ang, 0, Wf * 0.05, Ht * 0.08, Wf * 0.05);
    }
    out.gateY.push({ g, y, top: y + Ht });
  }


  /* ================================================== timber: Alas Purwo */

  /** Deterministic 0..1 from an integer — the stockade's logs are ragged, the
   *  same ragged on every boot. */
  const hash01 = (i) => { const v = Math.sin(i * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };
  const _qa = new THREE.Quaternion(), _qb = new THREE.Quaternion(), _ea = new THREE.Euler();
  const _ax = new THREE.Vector3(1, 0, 0), _ay = new THREE.Vector3(0, 1, 0);
  /** Euler XYZ (what GeoBuilder.put takes) for "yaw by `ang`, then pitch the
   *  box's own length up by `pitch`" — a rail that follows the slope. */
  function yawPitch(ang, pitch) {
    _qa.setFromAxisAngle(_ay, ang); _qb.setFromAxisAngle(_ax, -pitch);
    _qa.multiply(_qb); _ea.setFromQuaternion(_qa, 'XYZ');
    return [_ea.x, _ea.y, _ea.z];
  }

  /**
   * A timber stockade from (x0,z0) to (x1,z1): sharpened logs shoulder to
   * shoulder, each standing on ITS OWN ground and driven 0.9 m into it, so the
   * line steps down a slope log by log instead of floating off it; two rails
   * lashed across the inside and a heavier post every 3 m. `n = (-uz, ux)`
   * faces out, as for every wall run.
   */
  function palisadeRun(T, bw, x0, z0, x1, z1, h, seed, fires) {
    const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz);
    if (L < 0.2) return;
    const ux = dx / L, uz = dz / L, nx = -uz, nz = ux, ang = Math.atan2(dx, dz);
    const n = Math.max(1, Math.round(L / 0.47));
    for (let i = 0; i <= n; i++) {
      const t = i / n, x = x0 + dx * t, z = z0 + dz * t, y = T.heightAt(x, z);
      const hh = h * (0.88 + 0.22 * hash01(seed + i)), r = 0.22 + 0.05 * hash01(seed * 3 + i);
      const col = hash01(seed + i * 7) < 0.3 ? PAL.woodPale : (hash01(seed + i * 5) < 0.5 ? PAL.wood : PAL.woodDark);
      bw.put(CYL(6, 0.92), col, x, y - 0.9 + (hh + 0.9) * 0.5, z, 0, ang + i, 0, r, hh + 0.9, r, [1, 2]);
      bw.put(CONE(6), PAL.woodPale, x, y + hh + 0.26, z, 0, ang + i, 0, r * 0.98, 0.56, r * 0.98);
    }
    // rails and posts on the inner face, in 3 m pieces that follow the ground
    const m = Math.max(1, Math.round(L / 3));
    for (let k = 0; k < m; k++) {
      const a = k / m, b = (k + 1) / m;
      const ax = x0 + dx * a - nx * 0.34, az = z0 + dz * a - nz * 0.34;
      const bx = x0 + dx * b - nx * 0.34, bz = z0 + dz * b - nz * 0.34;
      const ya = T.heightAt(ax, az), yb = T.heightAt(bx, bz), seg = L / m;
      const [rx, ry, rz] = yawPitch(ang, Math.atan2(yb - ya, seg));
      for (const hy of [1.1, h * 0.78]) {
        bw.put(BOX(), PAL.woodDark, (ax + bx) / 2, (ya + yb) / 2 + hy, (az + bz) / 2, rx, ry, rz, 0.16, 0.20, seg * 1.04, seg * 0.4);
      }
      bw.put(CYL(6, 1), PAL.beamDark, ax, ya + h * 0.4, az, 0, 0, 0, 0.19, h * 0.8 + 0.9, 0.19, 1);
      // a brazier every 8th post on the inside, for a camp that expects no one
      if (fires && k % 8 === 4) {
        const fx = ax - nx * 2.0, fz = az - nz * 2.0, fy = T.heightAt(fx, fz);
        for (let q = 0; q < 3; q++) {
          const qa = q * 2.094;
          bw.put(CYL(4, 1), PAL.iron, fx + Math.cos(qa) * 0.28, fy + 0.5, fz + Math.sin(qa) * 0.28, Math.cos(qa) * 0.25, 0, Math.sin(qa) * 0.25, 0.05, 1.05, 0.05, 1);
        }
        bw.put(CYL(8, 1.3), PAL.iron, fx, fy + 1.02, fz, 0, 0, 0, 0.36, 0.18, 0.36, 1);
        fires.push({ x: fx, y: fy + 1.08, z: fz, s: 0.55, ring: false });
      }
    }
  }

  /** A timber regol: two log towers, a fighting bridge over the passage and a
   *  shingled roof over the bridge, torches either side of the mouth. */
  function buildRegol(T, g, out) {
    const bw = out.wood, br = out.roof;
    const G = g.tower, half = g.w * 0.5, tw = G.tw, D = g.t0 + G.proj * 2;
    const Ht = g.h * G.rise, hp = Ht * G.pass;
    const y = groundUnder(T, g.x, g.z, g.ux, g.uz, half + tw, D * 0.5 + 0.6);
    const ang = Math.atan2(g.ux, g.uz);
    const at = (along, across) => [g.x + g.ux * along + g.nx * across, g.z + g.uz * along + g.nz * across];
    for (const s of [-1, 1]) {
      const c = s * (half + tw * 0.5);
      // corner posts, the full height
      for (const pa of [-1, 1]) for (const pb of [-1, 1]) {
        const [px, pz] = at(c + pa * (tw * 0.5 - 0.2), pb * (D * 0.5 - 0.2));
        bw.put(CYL(6, 0.95), PAL.beamDark, px, y + Ht * 0.5 - 0.4, pz, 0, 0, 0, 0.24, Ht + 0.8, 0.24, [1, 2]);
      }
      // plank body up to the bridge, and horizontal bands across it
      const [bx, bz] = at(c, 0);
      bw.put(BOX(), PAL.wood, bx, y + hp * 0.5, bz, 0, ang, 0, D - 0.3, hp, tw - 0.3, [1.2, hp * 0.4]);
      for (const hy of [0.3, 0.62, 0.92]) {
        bw.put(BOX(), PAL.woodDark, bx, y + hp * hy, bz, 0, ang, 0, D - 0.1, 0.18, tw - 0.1, 1);
      }
      // torch at the mouth, on the outside face
      const [tx, tz] = at(s * (half + 0.3), D * 0.5 + 0.35);
      bw.put(CYL(4, 1), PAL.woodDark, tx, y + 1.6, tz, 0, 0, 0, 0.07, 3.2, 0.07, 1);
      out.fires.push({ x: tx, y: y + 3.25, z: tz, s: 0.42, ring: false });
    }
    // the bridge: floor, breastwork both faces, posts, roof over all of it
    const Wf = g.w + tw * 2;
    bw.put(BOX(), PAL.woodDark, g.x, y + hp, g.z, 0, ang, 0, D + 0.4, 0.34, Wf + 0.2, [Wf * 0.3, 1]);
    for (const s of [-1, 1]) {
      const [ex, ez] = at(0, s * (D * 0.5 + 0.1));
      bw.put(BOX(), PAL.wood, ex, y + hp + 0.75, ez, 0, ang, 0, 0.14, 1.2, Wf, [Wf * 0.3, 1]);
    }
    const top = y + Ht;
    bw.put(BOX(), PAL.beamDark, g.x, top - 0.3, g.z, 0, ang, 0, D + 0.3, 0.28, Wf + 0.3, 1);
    roofOn(br, g.x, top - 0.2, g.z, Wf + 0.6, D + 0.6, 0.9, 2.1, ang + Math.PI / 2, SHINGLE, 0.35);
    out.gateY.push({ g, y, top: top + 2.1 });
  }

  const SHINGLE = 0x4f3d2c, THATCH = 0x6e5b3c;

  /**
   * Bivouac: the Kidul host's lines on the plateau — no ring (the stockade is
   * the ring), a loose cluster of tents round two cook fires, spear racks,
   * sacks and a standard. `site.stores` makes it the camp's granary instead.
   */
  function buildBivouac(site, out) {
    const bw = out.wood, bc = out.cloth, br = out.roof;
    const r = (i) => hash01(site.x * 13 + site.z * 7 + i);
    const n = site.stores ? 3 : 7;
    for (let i = 0; i < n; i++) {
      const a = i * 0.9 + r(i) * 0.5, d = 7 + r(i + 20) * 6;
      const L = 4.0 + r(i + 40) * 1.6;
      tent(bw, bc, Math.cos(a) * d, Math.sin(a) * d, a + Math.PI / 2, L, L * 0.78, 2.3 + r(i + 60) * 0.5, i % 3 ? PAL.canvasA : PAL.canvasB);
    }
    // cook fires with a tripod and a pot
    for (const [fx, fz] of [[-2.5, 1.5], [3.5, -2.5]]) {
      for (let q = 0; q < 3; q++) {
        const qa = q * 2.094;
        bw.put(CYL(4, 1), PAL.woodDark, fx + Math.cos(qa) * 0.55, 0.85, fz + Math.sin(qa) * 0.55, Math.cos(qa) * 0.42, 0, Math.sin(qa) * 0.42, 0.05, 1.9, 0.05, 1);
      }
      bw.put(SPH(8, 6), PAL.iron, fx, 1.05, fz, 0, 0, 0, 0.62, 0.48, 0.62);
      out.fires.push({ x: fx, z: fz, s: 1.05 });
    }
    // spear racks
    for (const s of [-1, 1]) {
      const cx = s * 9.5, cz = 5;
      bw.put(BOX(), PAL.woodDark, cx, 1.2, cz, 0, 0, 0, 3.2, 0.12, 0.12, 1);
      for (let k = 0; k < 7; k++) {
        bw.put(CYL(4, 1), PAL.wood, cx - 1.4 + k * 0.46, 1.35, cz + 0.12, 0.18, 0, 0, 0.035, 2.7, 0.035, 1);
        bw.put(CONE(4), PAL.iron, cx - 1.4 + k * 0.46, 2.78, cz + 0.37, 0.18, 0, 0, 0.05, 0.3, 0.05);
      }
    }
    // sacks and crates
    for (let i = 0; i < (site.stores ? 26 : 9); i++) {
      const a = 2.4 + i * 0.37, d = (site.stores ? 4 : 11) + (i % 4) * 1.1;
      const sx = Math.cos(a) * d, sz = Math.sin(a) * d;
      if (i % 3) bw.put(SPH(7, 5), PAL.canvasDark, sx, 0.34 + (i % 5 === 0 ? 0.55 : 0), sz, 0, a, 0, 0.95, 0.62, 0.7);
      else bw.put(BOX(), PAL.wood, sx, 0.45, sz, 0, a, 0, 0.9, 0.9, 0.9, 1);
    }
    if (site.stores) {
      // lumbung: a rice barn on posts under a steep thatch
      for (const pa of [-1, 1]) for (const pb of [-1, 1]) bw.put(CYL(6, 1), PAL.beamDark, pa * 2.0, 1.1, -9 + pb * 1.6, 0, 0, 0, 0.18, 2.2, 0.18, 1);
      bw.put(BOX(), PAL.woodDark, 0, 2.35, -9, 0, 0, 0, 5.0, 0.3, 4.0, 2);
      bw.put(BOX(), PAL.woodPale, 0, 3.4, -9, 0, 0, 0, 4.4, 1.8, 3.4, [2, 1]);
      roofOn(br, 0, 4.2, -9, 4.6, 3.6, 0.9, 3.2, 0, THATCH, 0.15);
    }
    out.banners.push({ x: 0, z: -3, h: 7.5, s: 1.9 }, { x: -6, z: 9, h: 5.2, s: 1.4 });
    out.spawns.push({ x: 0, z: 8 }, { x: -7, z: 3 }, { x: 7, z: 3 }, { x: 0, z: -6 }, { x: -9, z: -7 }, { x: 9, z: -7 });
    out.radius = site.radius || 18;
  }

  /**
   * Pendapa: the Kidul command pavilion — an open joglo on a stone plinth,
   * twelve columns round four taller soko guru, a broad lower roof and the
   * steep upper one the four carry, a gong on its frame, braziers at the steps.
   */
  function buildPendapa(site, out) {
    const be = out.earth, bw = out.wood, br = out.roof;
    be.put(BOX(), PAL.stoneDark, 0, 0.25, 0, 0, 0, 0, 18.4, 1.3, 18.4, [4, 1]);
    be.put(BOX(), PAL.stone, 0, 0.95, 0, 0, 0, 0, 17.2, 0.3, 17.2, [4, 4]);
    for (let k = 0; k < 3; k++) be.put(BOX(), PAL.stonePale, 0, 0.2 + k * 0.28, 9.6 + (2 - k) * 0.55, 0, 0, 0, 5.2, 0.28, 0.6, 1);
    const post = (x, z, h, col) => {
      be.put(BOX(), PAL.stonePale, x, 1.25, z, 0, 0, 0, 0.62, 0.3, 0.62, 1);           // umpak
      bw.put(CYL(8, 0.94), col, x, 1.1 + h * 0.5, z, 0, 0, 0, 0.21, h, 0.21, [1, 2]);
    };
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) {
      if (i > 0 && i < 3 && j > 0 && j < 3) continue;
      post(-7.2 + i * 4.8, -7.2 + j * 4.8, 3.3, PAL.beam);
    }
    for (const a of [-1, 1]) for (const b of [-1, 1]) post(a * 2.4, b * 2.4, 5.8, PAL.beamDark);
    // tumpang sari: the stepped frame the soko guru carry
    for (let k = 0; k < 3; k++) bw.put(BOX(), PAL.woodDark, 0, 6.9 + k * 0.26, 0, 0, 0, 0, 5.6 + k * 0.5, 0.24, 5.6 + k * 0.5, 2);
    bw.put(BOX(), PAL.beamDark, 0, 4.45, 0, 0, 0, 0, 15.2, 0.3, 15.2, 3);                 // ring beam
    roofOn(br, 0, 4.5, 0, 14.6, 14.6, 1.6, 2.6, 0, SHINGLE, 0.12);
    roofOn(br, 0, 7.5, 0, 5.4, 3.4, 0.9, 5.2, 0, SHINGLE, 0.28);
    // the gong
    bw.put(BOX(), PAL.beamDark, -4.8, 2.9, 3.6, 0, 0, 0, 2.4, 0.16, 0.16, 1);
    for (const s of [-1, 1]) bw.put(BOX(), PAL.beamDark, -4.8 + s * 1.1, 2.0, 3.6, 0, 0, 0, 0.16, 1.9, 0.16, 1);
    bw.put(CYL(14, 1), PAL.gold, -4.8, 2.1, 3.6, Math.PI / 2, 0, 0, 0.72, 0.1, 0.72, 1);
    // braziers either side of the steps
    for (const s of [-1, 1]) {
      be.put(CYL(8, 1.25), PAL.stoneDark, s * 3.6, 1.6, 10.6, 0, 0, 0, 0.5, 0.9, 0.5, 1);
      out.fires.push({ x: s * 3.6, z: 10.6, s: 0.7, y: 1.95, noRing: true });
    }
    out.banners.push({ x: -9.8, z: 9.8, h: 10, s: 2.5 }, { x: 9.8, z: 9.8, h: 10, s: 2.5 },
      { x: -9.8, z: -9.8, h: 8, s: 2.0 }, { x: 9.8, z: -9.8, h: 8, s: 2.0 });
    out.spawns.push({ x: 0, z: 13 }, { x: -11, z: 6 }, { x: 11, z: 6 }, { x: -11, z: -8 }, { x: 11, z: -8 }, { x: 0, z: -12 });
    out.radius = site.radius || 20;
  }

  /** Watch: a panggung — a lookout on four tall stilts in the forest, a
   *  ladder, a thatched roof and a fire at its foot. */
  function buildWatch(site, out) {
    const bw = out.wood, br = out.roof;
    const H = 7.6;
    for (const a of [-1, 1]) for (const b of [-1, 1]) {
      bw.put(CYL(6, 0.9), PAL.beamDark, a * 1.9, H * 0.5 - 0.4, b * 1.9, 0, 0, 0, 0.21, H + 0.8, 0.21, [1, 2]);
    }
    for (const hy of [2.2, 4.8]) {
      for (const [x, z, rot] of [[0, 1.9, 0], [0, -1.9, 0], [1.9, 0, Math.PI / 2], [-1.9, 0, Math.PI / 2]]) {
        bw.put(BOX(), PAL.woodDark, x, hy, z, 0, rot, 0, 3.9, 0.14, 0.14, 1);
      }
    }
    bw.put(BOX(), PAL.wood, 0, H, 0, 0, 0, 0, 4.8, 0.26, 4.8, 2);
    for (const [x, z, rot] of [[0, 2.35, 0], [0, -2.35, 0], [2.35, 0, Math.PI / 2], [-2.35, 0, Math.PI / 2]]) {
      bw.put(BOX(), PAL.woodDark, x, H + 0.6, z, 0, rot, 0, 4.8, 0.1, 0.1, 1);
      bw.put(BOX(), PAL.wood, x, H + 0.3, z, 0, rot, 0, 4.8, 0.5, 0.06, 1);
    }
    for (const a of [-1, 1]) for (const b of [-1, 1]) bw.put(CYL(5, 1), PAL.beamDark, a * 2.1, H + 1.2, b * 2.1, 0, 0, 0, 0.1, 2.4, 0.1, 1);
    roofOn(br, 0, H + 2.3, 0, 4.4, 4.4, 0.9, 2.4, 0, THATCH, 0.2);
    // ladder
    for (const s of [-1, 1]) bw.put(CYL(4, 1), PAL.woodPale, s * 0.34, H * 0.5, 3.1, -0.22, 0, 0, 0.05, H + 0.4, 0.05, 1);
    for (let k = 1; k < 12; k++) bw.put(BOX(), PAL.woodPale, 0, k * (H / 12), 3.1 + 0.22 * (0.5 - k / 12) * H * 0.45, 0, 0, 0, 0.7, 0.06, 0.06, 1);
    out.fires.push({ x: 4.2, z: 3.2, s: 0.8 });
    out.banners.push({ x: -2.1, z: -2.1, h: 3.2, s: 1.3, y0: H + 0.1 });
    out.spawns.push({ x: 0, z: 7 }, { x: -6, z: 2 }, { x: 6, z: 2 }, { x: 0, z: -6 });
    out.radius = site.radius || 14;
  }

  /* ============================== assembly ============================= */

  /**
   * A banner mast: a slim tapered pole with iron collars, a yardarm the cloth
   * actually hangs from and a gilt spear finial on top. The cloth instance is
   * placed so its head sits just under the yardarm and its foot well clear of
   * the ground, so the flag reads as flown from the mast rather than pinned
   * halfway up an oversized post.
   *
   * `y0` is the height the mast stands on (0 = ground, or the rampart walk),
   * `h` its length, `s` the cloth scale, `ang` the cloth's local heading.
   */
  const BANNER_W = 1.28, BANNER_H = 2.62, BANNER_DROP = 0.72;
  function bannerPole(bw, x, z, y0, h, s, ang) {
    const top = y0 + h;
    bw.put(CYL(7, 0.62), PAL.woodDark, x, y0 + h * 0.5, z, 0, 0, 0, 0.115, h, 0.115, 3);
    for (let i = 1; i <= 4; i++)
      bw.put(CYL(7, 1), PAL.iron, x, y0 + h * (i / 5), z, 0, 0, 0, 0.135, 0.11, 0.135, 1);
    // base socket / earth collar
    bw.put(CYL(7, 0.8), PAL.iron, x, y0 + 0.28, z, 0, 0, 0, 0.26, 0.56, 0.26, 1);
    // yardarm the cloth hangs from, aligned with the cloth's own heading
    const L = s * BANNER_W * 1.04, ca = Math.cos(ang), sa = Math.sin(ang);
    bw.put(BOX(), PAL.woodDark, x + ca * L * 0.5, top - 0.42, z - sa * L * 0.5, 0, ang, 0, L, 0.10, 0.10, 1);
    bw.put(SPH(6, 5), PAL.gold, x + ca * L, top - 0.42, z - sa * L, 0, 0, 0, 0.17, 0.17, 0.17);
    // finial: collar, spear blade, tassel
    bw.put(CYL(7, 1), PAL.gold, x, top + 0.06, z, 0, 0, 0, 0.19, 0.20, 0.19, 1);
    bw.put(CONE(6), PAL.gold, x, top + 0.52, z, 0, 0.4, 0, 0.15, 0.80, 0.15);
    bw.put(SPH(6, 5), ORNAMENT.tassel, x + ca * 0.16, top - 0.06, z - sa * 0.16, 0, 0, 0, 0.16, 0.22, 0.16);
  }

  return {
    name: 'bases', order: ORDER.WORLD,
    init(w) {
      const T = w.terrain;
      const bannerSpecs = [];
      const fireSpecs = [];
      /**
       * Everything at a site that makes a noise, in world space, for the
       * ambience bed (`docs/REQUESTS.md`, audio row): braziers and camp fires,
       * banner cloth, the gate mouth, and one 'camp' marker per site so a bed
       * can be placed on the site as a whole. `site` is the base id, so audio
       * can drop an emitter when a base changes hands.
       */
      const emitters = [];

      for (const site of SITES) {
        const out = {
          earth: new GeoBuilder(), wood: new GeoBuilder(), roof: new GeoBuilder(), cloth: new GeoBuilder(),
          banners: [], fires: [], gates: [], spawns: [], radius: site.radius, noPad: false,
        };
        // `type` is the site's ROLE (fort / camp / post / bridge — mission,
        // director, minimap and stables all read it); `build` is its
        // ARCHITECTURE when the arena has its own. Without one they coincide.
        const build = site.build || site.type;
        if (build === 'fort') buildFort(site, out);
        else if (build === 'camp') buildCamp(site, out);
        else if (build === 'bridge') buildBridge(site, out, w);
        else if (build === 'post') buildPost(site, out);
        else if (build === 'bivouac') buildBivouac(site, out);
        else if (build === 'pendapa') buildPendapa(site, out);
        else if (build === 'watch') buildWatch(site, out);
        else buildOutpost(site, out);

        // every banner gets a mast, whether it stands on the ground, on the
        // rampart walk or on a gatehouse roof (`y0`)
        const CLOTH_ANG = 0.4;
        for (const b of out.banners) bannerPole(out.wood, b.x, b.z, b.y0 ?? 0, b.h, b.s, CLOTH_ANG);

        const baseY = site.type === 'bridge'
          ? (T.heightAt(site.x, site.z) + 5.2)
          : (site.padY !== undefined ? site.padY : T.heightAt(site.x, site.z));

        const g = new THREE.Group();
        g.position.set(site.x, baseY, site.z);
        if (site.type === 'bridge') {
          // The deck is geometry only — `terrain.heightAt()` still returns the
          // gorge floor 5.2 m beneath it, and everything that walks is clamped
          // to that, so a squad ordered across the Brantas fords the river and
          // passes UNDER its own arches. Register the clear span as a walkable
          // surface: the deck slab tops out at deckY + 0.08 and the parapets
          // start at |z| = 4.35, and the approach ramps blend to the bank over
          // 6 m so a body walks up onto it instead of hitting a 2 m step.
          w.terrain.addWalkway?.({
            x: site.x, z: site.z, rot: site.rot, tag: site.id,
            hx: 21.0, hz: 4.3, y: baseY + 0.08, ramp: 6.0, feather: 0.9,
          });
        }
        g.rotation.y = site.rot;
        g.name = 'base_' + site.id;
        g.matrixAutoUpdate = false;
        g.updateMatrix(); g.updateMatrixWorld(true);

        const add = (builder, mat, name) => {
          if (builder.count === 0) return;
          const m = new THREE.Mesh(builder.toGeometry(), mat);
          m.castShadow = true; m.receiveShadow = true; m.name = site.id + '_' + name;
          m.matrixAutoUpdate = false; m.updateMatrix();
          g.add(m);
        };
        add(out.earth, matEarth, 'earth');
        add(out.wood, matWood, 'wood');
        add(out.roof, matRoof, 'roof');
        add(out.cloth, matCloth, 'cloth');
        group.add(g);

        // world-space banner + fire placement
        const ca = Math.cos(site.rot), sa = Math.sin(site.rot);
        const toWorld = (lx, lz) => ({ x: site.x + lx * ca + lz * sa, z: site.z - lx * sa + lz * ca });
        const start = bannerSpecs.length;
        for (const b of out.banners) {
          const p = toWorld(b.x, b.z);
          // hang the cloth from just under the yardarm: its head is
          // BANNER_DROP below the mast top, and the instance origin is the
          // centre of a cloth that is s*BANNER_H tall
          const top = baseY + (b.y0 ?? 0) + b.h;
          bannerSpecs.push({
            x: p.x, y: top - BANNER_DROP - b.s * BANNER_H * 0.5, z: p.z,
            s: b.s, rot: site.rot + CLOTH_ANG, site,
          });
        }
        banners.ranges.set(site.id, [start, bannerSpecs.length]);
        for (const f of out.fires) {
          const p = toWorld(f.x, f.z);
          fireSpecs.push({ x: p.x, y: baseY + 0.15 + (f.y ?? 0), z: p.z, s: f.s, ring: !f.noRing });
          emitters.push({ kind: 'fire', x: p.x, y: baseY + 0.6 + (f.y ?? 0), z: p.z, r: 2.2 * f.s, site: site.id });
        }
        for (const b of out.banners) {
          const p = toWorld(b.x, b.z);
          emitters.push({ kind: 'banner', x: p.x, y: baseY + (b.y0 ?? 0) + b.h - BANNER_DROP, z: p.z, r: 1.4 * b.s, site: site.id });
        }
        for (const g2 of out.gates) {
          const p = toWorld(g2.x, g2.z);
          emitters.push({ kind: 'gate', x: p.x, y: baseY + g2.y, z: p.z, r: g2.r, site: site.id });
        }
        emitters.push({ kind: 'camp', x: site.x, y: baseY + 1.0, z: site.z, r: out.radius, site: site.id });

        const spawns = out.spawns.map(s => { const p = toWorld(s.x, s.z); return { x: p.x, z: p.z }; });
        list.push({
          id: site.id, name: site.name, x: site.x, z: site.z, y: baseY,
          radius: out.radius, team: site.team, type: site.type, spawns, group: g, site,
        });
      }

      /* ---- the arena's own walls, if it has any ---- */
      if (WALLS.n) {
        const out = { earth: new GeoBuilder(), wood: new GeoBuilder(), roof: new GeoBuilder(), gateY: [], fires: [] };
        buildArenaWalls(T, out);
        const g = new THREE.Group(); g.name = 'arena_walls';
        g.matrixAutoUpdate = false; g.updateMatrix();
        for (const [builder, mat, name] of [[out.earth, matEarth, 'masonry'], [out.wood, matWood, 'timber'], [out.roof, matRoof, 'roof']]) {
          if (builder.count === 0) continue;
          const m = new THREE.Mesh(builder.toGeometry(), mat);
          m.castShadow = true; m.receiveShadow = true; m.name = 'walls_' + name;
          m.matrixAutoUpdate = false; m.updateMatrix();
          g.add(m);
        }
        group.add(g);
        wallGroup = g;
        for (const q of out.gateY) {
          emitters.push({ kind: 'gate', x: q.g.x, y: q.y + 3, z: q.g.z, r: q.g.w * 0.6, site: null });
        }
        // torches and braziers are already in world space
        for (const f of out.fires) {
          fireSpecs.push(f);
          emitters.push({ kind: 'fire', x: f.x, y: f.y + 0.5, z: f.z, r: 2.2 * f.s, site: null });
        }
      }

      /* ---- banners: one instanced, vertex-animated cloth for the map ---- */
      {
        const CW = 6, CH = 5;
        const geo = new THREE.PlaneGeometry(1, 1, CW, CH);
        geo.translate(0.5, 0, 0);                  // x: 0 at pole → 1 at fly
        const uni = THREE.UniformsUtils.merge([THREE.UniformsLib.lights, THREE.UniformsLib.fog, {
          uTime: { value: 0 }, uTex: { value: null },
          uTrim: { value: new THREE.Color() }, uGlyph: { value: new THREE.Color() },
        }]);
        uni.uTex.value = bannerTex();
        uni.uTrim.value.setHex(0xe6c874);
        uni.uGlyph.value.setHex(0xf6efdc);
        bannerUni = uni;
        const mat = new THREE.ShaderMaterial({
          uniforms: uni,
          lights: true, fog: true, side: THREE.DoubleSide,
          vertexShader: `
            #include <common>
            #include <fog_pars_vertex>
            uniform float uTime;
            varying vec2 vUv;
            varying vec3 vNrm;
            varying vec3 vCol;
            void main() {
              vUv = uv;
              vCol = instanceColor;
              vec3 p = position;
              float ph = instanceMatrix[3][0] * 0.23 + instanceMatrix[3][2] * 0.17;
              float t = uTime * 2.2 + ph;
              float amp = p.x * p.x;
              float wv = sin(p.x * 6.5 - t * 2.6) * 0.15 + sin(p.x * 3.0 + p.y * 3.4 - t * 1.7) * 0.10;
              p.z += wv * amp;
              p.y += sin(p.x * 4.2 - t * 2.2) * 0.05 * amp;
              float dw = cos(p.x * 6.5 - t * 2.6) * 6.5 * 0.15 + cos(p.x * 3.0 + p.y * 3.4 - t * 1.7) * 3.0 * 0.10;
              vec3 n = normalize(vec3(-dw * amp, 0.15, 1.0));
              vec4 wp = modelMatrix * instanceMatrix * vec4(p, 1.0);
              vNrm = normalize((viewMatrix * modelMatrix * instanceMatrix * vec4(n, 0.0)).xyz);
              vec4 mv = viewMatrix * wp;
              gl_Position = projectionMatrix * mv;
              vec4 mvPosition = mv;
              #include <fog_vertex>
            }`,
          fragmentShader: `
            #include <common>
            #include <packing>
            #include <fog_pars_fragment>
            #include <lights_pars_begin>
            uniform sampler2D uTex;
            uniform vec3 uTrim;
            uniform vec3 uGlyph;
            varying vec2 vUv;
            varying vec3 vNrm;
            varying vec3 vCol;
            void main() {
              vec4 tx = texture2D(uTex, vUv);
              vec3 col = vCol * (0.55 + 0.75 * tx.r);
              col = mix(col, uTrim * (0.6 + 0.6 * tx.r), tx.g);
              col = mix(col, uGlyph * (0.7 + 0.5 * tx.r), tx.b);
              vec3 n = normalize(vNrm);
              if (!gl_FrontFacing) n = -n;
              vec3 lit = ambientLightColor + vec3(0.30, 0.33, 0.38);
              #if NUM_HEMI_LIGHTS > 0
                float hd = 0.5 * dot(n, hemisphereLights[0].direction) + 0.5;
                lit += mix(hemisphereLights[0].groundColor, hemisphereLights[0].skyColor, hd);
              #endif
              #if NUM_DIR_LIGHTS > 0
                float ndl = abs(dot(n, directionalLights[0].direction));
                lit += directionalLights[0].color * (0.25 + 0.85 * ndl);
              #endif
              gl_FragColor = vec4(col * lit * RECIPROCAL_PI, 1.0);
              #include <tonemapping_fragment>
              #include <colorspace_fragment>
              #include <fog_fragment>
            }`,
        });
        const im = new THREE.InstancedMesh(geo, mat, Math.max(1, bannerSpecs.length));
        const d = new THREE.Object3D(), col = new THREE.Color();
        for (let i = 0; i < bannerSpecs.length; i++) {
          const b = bannerSpecs[i];
          d.position.set(b.x, b.y, b.z);
          d.rotation.set(0, b.rot, 0);
          d.scale.set(b.s * BANNER_W, b.s * BANNER_H, b.s);
          d.updateMatrix();
          im.setMatrixAt(i, d.matrix);
          im.setColorAt(i, col.setHex(FACTION[b.site.team].cloth));
        }
        im.count = bannerSpecs.length;
        im.instanceMatrix.needsUpdate = true;
        if (im.instanceColor) im.instanceColor.needsUpdate = true;
        im.castShadow = false; im.receiveShadow = false;
        im.frustumCulled = false;
        im.name = 'banners';
        banners.mesh = im;
        group.add(im);
      }

      /* ------------------------------ campfires ------------------------- */
      if (fireSpecs.length) {
        const gb = new GeoBuilder();
        const rng = w.rng.fork(0x77aa);
        for (const f of fireSpecs) {
          if (!f.ring) continue;
          for (let i = 0; i < 9; i++) {
            const a = (i / 9) * 6.283;
            gb.put(rockGeo(0, 7 + i, 0.4), 0x6e6a62, f.x + Math.cos(a) * 1.05 * f.s, f.y + 0.12, f.z + Math.sin(a) * 1.05 * f.s,
              rng.next(), rng.next() * 3, rng.next(), 0.55 * f.s, 0.4 * f.s, 0.55 * f.s);
          }
          for (let i = 0; i < 5; i++) {
            const a = (i / 5) * 6.283 + 0.4;
            gb.put(CYL(4, 0.8), 0x3a2c1e, f.x, f.y + 0.35 * f.s, f.z, Math.cos(a) * 0.9, a, Math.sin(a) * 0.9, 0.08, 1.5 * f.s, 0.08);
          }
        }
        const ring = new THREE.Mesh(gb.toGeometry(), matWood);
        ring.castShadow = true; ring.receiveShadow = true; ring.name = 'firepits';
        group.add(ring);

        const fg = new THREE.PlaneGeometry(1, 1);
        fg.translate(0, 0.5, 0);
        fireUni = { uTime: { value: 0 }, uTex: { value: fireTex() } };
        const fmat = new THREE.ShaderMaterial({
          uniforms: fireUni, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
          side: THREE.DoubleSide,
          vertexShader: `
            uniform float uTime;
            varying vec2 vUv; varying float vPh;
            void main() {
              vUv = uv;
              vec3 p = position;
              float ph = instanceMatrix[3][0] * 1.7 + instanceMatrix[3][2] * 2.3;
              vPh = ph;
              float f = sin(uTime * 9.0 + ph) * 0.10 + sin(uTime * 14.0 + ph * 2.1) * 0.06;
              p.x += f * uv.y * 1.4;
              p.y *= 1.0 + f * 0.7;
              vec4 wp = modelMatrix * instanceMatrix * vec4(p, 1.0);
              vec4 mv = viewMatrix * wp;
              gl_Position = projectionMatrix * mv;
            }`,
          fragmentShader: `
            uniform sampler2D uTex; uniform float uTime;
            varying vec2 vUv; varying float vPh;
            void main() {
              vec4 t = texture2D(uTex, vUv);
              float flick = 0.72 + 0.34 * sin(uTime * 17.0 + vPh * 3.0);
              gl_FragColor = vec4(t.rgb * flick * 1.9, t.a * flick);
              if (gl_FragColor.a < 0.02) discard;
            }`,
        });
        const N = fireSpecs.length * 3;
        const fm = new THREE.InstancedMesh(fg, fmat, N);
        const d = new THREE.Object3D();
        let k = 0;
        for (const f of fireSpecs) for (let i = 0; i < 3; i++) {
          d.position.set(f.x + (i - 1) * 0.14 * f.s, f.y + 0.15, f.z + (i - 1) * 0.1 * f.s);
          d.rotation.set(0, i * 1.05, 0);
          d.scale.set(1.5 * f.s, (1.9 + i * 0.25) * f.s, 1.5 * f.s);
          d.updateMatrix();
          fm.setMatrixAt(k++, d.matrix);
        }
        fm.count = k;
        fm.instanceMatrix.needsUpdate = true;
        fm.frustumCulled = false; fm.renderOrder = 6;
        fireMesh = fm;
        group.add(fm);

        /* Firelight. A fire here is flame quads and nothing else — no light
         * — so a night stage got orange pinpricks in a blue-grey field (Alas
         * Purwo's first capture: not one warm pixel on the ground). This is
         * the cheap version of a light: an additive pool on the ground under
         * every fire, which the post chain's bloom then carries. It exists
         * only at NIGHT (update() reads the lighting mood) and is hidden
         * otherwise, so every daylight and dusk frame is exactly as before. */
        const cv = canvas(64);
        const grad = cv.g.createRadialGradient(32, 32, 0, 32, 32, 32);
        grad.addColorStop(0, 'rgba(255,196,120,1)');
        grad.addColorStop(0.3, 'rgba(255,140,60,.55)');
        grad.addColorStop(0.7, 'rgba(230,90,30,.14)');
        grad.addColorStop(1, 'rgba(200,70,20,0)');
        cv.g.fillStyle = grad; cv.g.fillRect(0, 0, 64, 64);
        const gmat = new THREE.MeshBasicMaterial({
          map: canvasTexture(cv.c, { repeat: false }), transparent: true, opacity: 0,
          blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
          polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
        });
        const pg = new THREE.PlaneGeometry(1, 1); pg.rotateX(-Math.PI / 2);
        const gm = new THREE.InstancedMesh(pg, gmat, fireSpecs.length);
        const T = w.terrain;
        for (let i = 0; i < fireSpecs.length; i++) {
          const f = fireSpecs[i];
          const gy = T ? T.heightAt(f.x, f.z) : f.y;
          d.position.set(f.x, Math.max(gy, f.y - 2.5) + 0.14, f.z);
          d.rotation.set(0, i * 0.7, 0);
          const r = 13 * f.s;
          d.scale.set(r, 1, r);
          d.updateMatrix();
          gm.setMatrixAt(i, d.matrix);
        }
        gm.instanceMatrix.needsUpdate = true;
        gm.frustumCulled = false; gm.renderOrder = 5; gm.visible = false; gm.name = 'firelight';
        glowMesh = gm;
        group.add(gm);
      }

      w.scene.add(group);

      const api = {
        list, group, emitters,
        /** The arena's own walls (world space), or null on open ground. */
        walls: wallGroup,
        nearest(x, z) {
          let best = null, bd = 1e18;
          for (const b of list) { const d = Math.hypot(x - b.x, z - b.z); if (d < bd) { bd = d; best = b; } }
          return best;
        },
        inRadius(x, z) {
          for (const b of list) if (Math.hypot(x - b.x, z - b.z) < b.radius) return b;
          return null;
        },
        setTeam(id, team) {
          const b = list.find(v => v.id === id);
          if (!b) return;
          b.team = team; b.site.team = team;
          const r = banners.ranges.get(id);
          if (r && banners.mesh) {
            const c = banners.color.setHex(FACTION[team % FACTION.length].cloth);
            for (let i = r[0]; i < r[1]; i++) banners.mesh.setColorAt(i, c);
            if (banners.mesh.instanceColor) banners.mesh.instanceColor.needsUpdate = true;
          }
          w.bus?.emit?.('base:captured', b);
        },
        capture(id, team) { api.setTeam(id, team); },
        banners: banners.mesh,
      };
      w.bases = api;
      /* The arena's walls as a world API — collision, sight, routing — for the
       * probes and any system that would rather not import a world module. */
      w.walls = WALLS;
      if (w.mission) w.mission.bases = list;
    },
    update(w, dt, alpha, realDt) {
      if (bannerUni) bannerUni.uTime.value += realDt;
      if (fireUni) fireUni.uTime.value += realDt;
      if (glowMesh) {
        const night = w.lighting && w.lighting.mood === 'night-fire';
        glowMesh.visible = night;
        if (night) {
          const t = fireUni ? fireUni.uTime.value : 0;
          glowMesh.material.opacity = 0.62 + 0.08 * Math.sin(t * 7.3) + 0.05 * Math.sin(t * 13.1);
        }
      }
    },
    dispose() {
      group.traverse(o => { o.geometry?.dispose?.(); });
      matEarth.dispose(); matWood.dispose(); matRoof.dispose(); matCloth.dispose();
      texEarth.dispose(); texEarthN.dispose(); texWood.dispose(); texRoof.dispose(); texCloth.dispose();
    },
  };
}
